import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { formatCurrency, formatDate, parseLocalDate, getStatusBadgeClass, getStatusLabel, isOverdue, PAYMENT_METHODS } from '../lib/utils';
import { Button } from '../components/ui/button';
import NotesCell from '../components/NotesCell';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Switch } from '../components/ui/switch';
import { Checkbox } from '../components/ui/checkbox';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Separator } from '../components/ui/separator';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { toast } from 'sonner';
import { Plus, Search, Filter, Check, CheckCircle, Loader2, Edit2, Trash2, Eye, ChevronDown, ChevronRight, ChevronLeft, Calendar, Copy, DollarSign, ArrowUpDown, ArrowUp, ArrowDown, Download, X, RotateCcw, Wrench, Layers } from 'lucide-react';
import axios from 'axios';
import { useAuthStore, useAccountFilterStore } from '../store';
import ColumnSelector from '../components/ColumnSelector';
import { AdvancedFilters, applyAdvancedFilters } from '../components/AdvancedFilters';
import { exportToExcel, EXPORT_CONFIGS } from '../lib/exportExcel';
import { generateLedgerPdf } from '../lib/ledgerPdf';
import ValidateExportDialog from '../components/ValidateExportDialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import {
  ActiveFilterChips, MonthNavigator, SearchInput, MoreFiltersButton,
  ExportDropdown, AdminToolsDropdown, buildFilterChips,
  ClickableSummaryCards, QuickPresets, QuickFilters, GroupByDropdown,
} from '../components/LedgerToolbar';
import { applyLedgerClientFilters, groupLedgerItems } from '../hooks/useLedgerFilters';

import { API_URL } from '../lib/api';

// Helper to get current month in YYYY-MM format
const getCurrentMonth = () => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
};

// Helper to format month for display
const formatMonth = (monthStr) => {
  const [year, month] = monthStr.split('-');
  const months = ['Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho', 
                  'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'];
  return `${months[parseInt(month) - 1]} ${year}`;
};

// Helper to change month
const changeMonth = (monthStr, delta) => {
  const [year, month] = monthStr.split('-').map(Number);
  let newMonth = month + delta;
  let newYear = year;
  if (newMonth > 12) { newMonth = 1; newYear++; }
  if (newMonth < 1) { newMonth = 12; newYear--; }
  return `${newYear}-${String(newMonth).padStart(2, '0')}`;
};

// Column definitions for visibility toggle
const PAYABLE_COLUMNS = [
  { key: 'obs', label: 'Obs', essential: false },
  { key: 'checkbox', label: 'Seleção', essential: false },
  { key: 'due_date', label: 'Vencimento', essential: true },
  { key: 'forecast_date', label: 'Previsão', essential: false },
  { key: 'payment_date', label: 'Pagamento', essential: false },
  { key: 'vendor_name', label: 'Fornecedor', essential: true },
  { key: 'category', label: 'Categoria', essential: false },
  { key: 'description', label: 'Descrição', essential: true },
  { key: 'invoice_number', label: 'Nº Doc', essential: false },
  { key: 'nf_number', label: 'Nº NF', essential: false },
  { key: 'bank_account', label: 'Conta', essential: false },
  { key: 'amount', label: 'Valor', essential: true },
  { key: 'paid_amount', label: 'Pago', essential: false },
  { key: 'status', label: 'Status', essential: true },
  { key: 'actions', label: 'Ações', essential: true },
];

// Get initial visible columns from localStorage or default to ESSENTIAL ONLY
// (Previsão, Pagamento, Nº Doc, Nº NF, Conta, Pago ficam ocultas por padrão — usuário habilita em "Colunas")
const DEFAULT_VISIBLE = PAYABLE_COLUMNS.filter(c => c.essential).map(c => c.key).concat(['obs', 'checkbox', 'category']);
const COLUMN_SCHEMA_VERSION = '2026-05';
const getInitialVisibleColumns = () => {
  const savedVersion = localStorage.getItem('payables_columns_schema');
  const saved = localStorage.getItem('payables_visible_columns');
  // Se schema mudou, resetar para o novo default (visual limpo)
  if (savedVersion !== COLUMN_SCHEMA_VERSION) {
    localStorage.setItem('payables_columns_schema', COLUMN_SCHEMA_VERSION);
    localStorage.setItem('payables_visible_columns', JSON.stringify(DEFAULT_VISIBLE));
    return DEFAULT_VISIBLE;
  }
  if (saved) {
    try {
      return JSON.parse(saved);
    } catch (e) {
      return DEFAULT_VISIBLE;
    }
  }
  return DEFAULT_VISIBLE;
};

export const PayablesPage = () => {
  const { token, user, tenant } = useAuthStore();
  const isAdmin = user?.role === 'admin';
  const { getFilterParam, allSelected, selectedAccountIds } = useAccountFilterStore();
  const [payables, setPayables] = useState([]);
  const [payablesLoading, setPayablesLoading] = useState(true);
  const [payablesSummary, setPayablesSummary] = useState({});
  const [categories, setCategories] = useState([]);
  const [costCenters, setCostCenters] = useState([]);
  const [bankAccounts, setBankAccounts] = useState([]);
  
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isPayOpen, setIsPayOpen] = useState(false);
  const [isViewOpen, setIsViewOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [deleteRecurringFuture, setDeleteRecurringFuture] = useState(false);
  const [deleteRecurringPast, setDeleteRecurringPast] = useState(false);
  const [isInstallmentEditOpen, setIsInstallmentEditOpen] = useState(false);
  const [isReplicateOpen, setIsReplicateOpen] = useState(false);
  // Parcelar saldo restante (negociação de parcelamento)
  const [isParcelOpen, setIsParcelOpen] = useState(false);
  const [parcelCount, setParcelCount] = useState(2);
  const [parcelFirstDate, setParcelFirstDate] = useState('');
  const [parcelAmounts, setParcelAmounts] = useState([]); // valores individuais
  const [parcelUseEqual, setParcelUseEqual] = useState(true);
  const [selectedPayable, setSelectedPayable] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [replicateDate, setReplicateDate] = useState('');
  const [replicateAmount, setReplicateAmount] = useState('');
  const [replicateDescription, setReplicateDescription] = useState('');
  
  const [filterStatus, setFilterStatus] = useState(() => localStorage.getItem('payables_filterStatus') || 'unpaid');
  const [filterInstallment, setFilterInstallment] = useState(() => localStorage.getItem('payables_filterInstallment') || 'all');
  const [filterMonth, setFilterMonth] = useState(() => localStorage.getItem('payables_filterMonth') || getCurrentMonth());
  const [filterMonthRange, setFilterMonthRange] = useState(() => {
    const v = localStorage.getItem('payables_filterMonthRange');
    return v !== null ? parseInt(v) : 0;
  });
  const [dateFilterField, setDateFilterField] = useState(() => localStorage.getItem('payables_dateFilterField') || 'due_date');
  const [customStartDate, setCustomStartDate] = useState(() => localStorage.getItem('payables_customStart') || '');
  const [customEndDate, setCustomEndDate] = useState(() => localStorage.getItem('payables_customEnd') || '');
  const [exportValidatorOpen, setExportValidatorOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedGroups, setExpandedGroups] = useState({});
  const [groupInstallments, setGroupInstallments] = useState(() => localStorage.getItem('payables_groupInstallments') === 'true');

  // ==== Novos filtros rápidos (quick filters) ====
  const [vendorFilter, setVendorFilter] = useState(() => localStorage.getItem('payables_vendorFilter') || 'all');
  const [categoryFilter, setCategoryFilter] = useState(() => localStorage.getItem('payables_categoryFilter') || 'all');
  const [costCenterFilter, setCostCenterFilter] = useState(() => localStorage.getItem('payables_costCenterFilter') || 'all');
  const [groupBy, setGroupBy] = useState(() => localStorage.getItem('payables_groupBy') || 'none');
  // Default: abrir mostrando Vencidos + Vence hoje. Usuário pode desativar clicando no chip.
  const [quickPreset, setQuickPreset] = useState(() => {
    const v = localStorage.getItem('payables_quickPreset');
    return v === null ? 'overdue_today' : (v === '__null__' ? null : v);
  });

  // Persistir filtros-chave
  useEffect(() => { localStorage.setItem('payables_filterStatus', filterStatus); }, [filterStatus]);
  useEffect(() => { localStorage.setItem('payables_filterInstallment', filterInstallment); }, [filterInstallment]);
  useEffect(() => { localStorage.setItem('payables_filterMonth', filterMonth); }, [filterMonth]);
  useEffect(() => { localStorage.setItem('payables_filterMonthRange', String(filterMonthRange)); }, [filterMonthRange]);
  useEffect(() => { localStorage.setItem('payables_dateFilterField', dateFilterField); }, [dateFilterField]);
  useEffect(() => { localStorage.setItem('payables_customStart', customStartDate); }, [customStartDate]);
  useEffect(() => { localStorage.setItem('payables_customEnd', customEndDate); }, [customEndDate]);
  useEffect(() => { localStorage.setItem('payables_groupInstallments', String(groupInstallments)); }, [groupInstallments]);
  useEffect(() => { localStorage.setItem('payables_vendorFilter', vendorFilter); }, [vendorFilter]);
  useEffect(() => { localStorage.setItem('payables_categoryFilter', categoryFilter); }, [categoryFilter]);
  useEffect(() => { localStorage.setItem('payables_costCenterFilter', costCenterFilter); }, [costCenterFilter]);
  useEffect(() => { localStorage.setItem('payables_groupBy', groupBy); }, [groupBy]);
  useEffect(() => { localStorage.setItem('payables_quickPreset', quickPreset === null ? '__null__' : quickPreset); }, [quickPreset]);
  
  // Sorting state
  const [sortConfig, setSortConfig] = useState({ key: 'due_date', direction: 'asc' });
  
  // Column visibility state
  const [visibleColumns, setVisibleColumns] = useState(getInitialVisibleColumns);
  const isColumnVisible = (key) => visibleColumns.includes(key);
  
  // Advanced filters state
  const [advancedFilters, setAdvancedFilters] = useState({});
  
  // Date field options for filter dropdown
  const dateFieldOptions = [
    { value: 'due_date', label: 'Vencimento' },
    { value: 'payment_date', label: 'Pagamento' },
    { value: 'forecast_date', label: 'Previsão' },
  ];
  
  // Multi-select state
  const [selectedItems, setSelectedItems] = useState([]);
  const [isBulkDeleteOpen, setIsBulkDeleteOpen] = useState(false);
  const [isBulkEditOpen, setIsBulkEditOpen] = useState(false);
  const [isBulkPayOpen, setIsBulkPayOpen] = useState(false);
  const [bulkEditData, setBulkEditData] = useState({
    category_id: '',
    cost_center_id: '',
    forecast_date: '',
    competence_date: '',
    notes: '',
    amount: ''
  });
  const [bulkPayData, setBulkPayData] = useState({
    bank_account_id: '',
    payment_date: new Date().toISOString().split('T')[0],
    payment_method: 'transfer'
  });

  // Local state for comments - for inline editing
  const [localComments, setLocalComments] = useState({});

  const headers = { Authorization: `Bearer ${token}` };

  // Filter columns config for advanced filters
  const filterableColumns = useMemo(() => [
    { key: 'due_date', label: 'Vencimento' },
    { key: 'forecast_date', label: 'Previsão' },
    { key: 'payment_date', label: 'Pagamento' },
    { key: 'vendor_name', label: 'Fornecedor' },
    { key: 'description', label: 'Descrição' },
    { key: 'category', label: 'Categoria' },
    { key: 'amount', label: 'Valor' },
  ], []);

  const dateColumns = ['due_date', 'forecast_date', 'payment_date'];

  // Save comment to backend
  const saveComment = async (payableId, text) => {
    try {
      await axios.patch(`${API_URL}/api/finance/payables/${payableId}/comment`, 
        { notes: text },
        { headers }
      );
      // Update local payables state
      setPayables(prev => prev.map(p => p.id === payableId ? { ...p, notes: text } : p));
    } catch (error) {
      console.error('Error saving comment:', error);
      toast.error('Erro ao salvar comentário');
    }
  };

  // Form states
  const [formData, setFormData] = useState({
    vendor_name: '', description: '', category_id: '', cost_center_id: '',
    competence_date: '', invoice_date: '', invoice_number: '', nf_number: '', due_date: '', 
    forecast_date: '', // Data de previsão para fluxo de caixa
    amount: '', payment_method: '', notes: '',
    bank_account_id: '', // Conta bancária de destino (para filtro e fluxo de caixa)
    is_installment: false, total_amount: '', num_installments: '', first_due_date: ''
  });

  const [editFormData, setEditFormData] = useState({});
  const [installmentEditData, setInstallmentEditData] = useState({
    scope: 'single', description: '', vendor_name: '', category_id: '', category: '',
    cost_center_id: '', payment_method: '', amount: '', notes: '',
    bank_account_id: '', invoice_number: '', nf_number: '', invoice_date: '', competence_date: '',
    due_date: '', forecast_date: ''
  });
  
  const [isCreateCategoryOpen, setIsCreateCategoryOpen] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState('');
  
  const [isCreateVendorOpen, setIsCreateVendorOpen] = useState(false);
  const [newVendorData, setNewVendorData] = useState({ name: '', document: '', phone: '', email: '' });
  const [vendors, setVendors] = useState([]);
  
  const [isCreateCostCenterOpen, setIsCreateCostCenterOpen] = useState(false);
  const [newCostCenterData, setNewCostCenterData] = useState({ code: '', name: '' });

  const [payFormData, setPayFormData] = useState({
    paid_amount: '', 
    paid_date: new Date().toISOString().split('T')[0], 
    bank_account_id: '',
    penalty_amount: '',
    interest_amount: '',
    discount_amount: '',
    notes: '',
    skip_bank_transaction: false // Não criar transação bancária automática (para quem usa extrato importado)
  });

  const fetchPayables = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setPayablesLoading(true);
    if (!silent) setSelectedItems([]); // Clear selection when fetching fresh from server
    try {
      const params = new URLSearchParams();
      if (filterStatus !== 'all') params.append('status', filterStatus);
      if (filterInstallment === 'installments_only') params.append('installment_filter', 'installments_only');
      if (filterInstallment === 'single_only') params.append('installment_filter', 'single_only');
      
      // Add date filter field parameter
      params.append('date_filter_field', dateFilterField);
      
      // Only apply month filter if not "Todos" (0) and not custom (-1)
      if (filterMonthRange === -1) {
        // Custom range
        if (customStartDate) params.append('start_date', customStartDate);
        if (customEndDate) params.append('end_date', customEndDate);
        params.append('limit', '100000');
      } else if (filterMonthRange !== 0) {
        if (filterMonth) params.append('month', filterMonth);
        params.append('month_range', filterMonthRange.toString());
      } else {
        // When "Todos" selected, fetch ALL records (no limit truncation)
        params.append('limit', '100000');
      }
      
      // Apply bank account filter from global state
      const accountFilter = getFilterParam();
      if (accountFilter) {
        params.append('account_ids', accountFilter);
      }
      
      const res = await axios.get(`${API_URL}/api/finance/payables?${params}`, { headers });
      setPayables(res.data.payables);
      setPayablesSummary(res.data.summary || {});
    } catch (error) {
      if (!silent) toast.error('Erro ao carregar contas a pagar');
    } finally {
      if (!silent) setPayablesLoading(false);
    }
  }, [token, filterStatus, filterInstallment, filterMonth, filterMonthRange, dateFilterField, customStartDate, customEndDate, allSelected, selectedAccountIds]);

  const fetchCategories = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/finance/categories?type=expense`, { headers });
      // API returns array directly, or object with categories key
      const data = res.data;
      setCategories(Array.isArray(data) ? data : (data.categories || []));
    } catch (e) { console.error(e); }
  };

  const fetchCostCenters = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/finance/cost-centers`, { headers });
      // API returns array directly, or object with cost_centers key
      const data = res.data;
      setCostCenters(Array.isArray(data) ? data : (data.cost_centers || []));
    } catch (e) { console.error(e); }
  };

  const fetchVendors = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/finance/vendors`, { headers });
      setVendors(Array.isArray(res.data) ? res.data : []);
    } catch (e) { console.error(e); }
  };

  const fetchBankAccounts = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/finance/bank-accounts`, { headers });
      const data = res.data;
      // API returns array directly, or object with accounts/bank_accounts key
      setBankAccounts(Array.isArray(data) ? data : (data.accounts || data.bank_accounts || []));
    } catch (e) { console.error(e); }
  };

  useEffect(() => {
    fetchPayables();
    fetchCategories();
    fetchCostCenters();
    fetchBankAccounts();
    fetchVendors();
  }, [fetchPayables]);

  const resetFormData = () => {
    setFormData({
      vendor_name: '', vendor_id: '', description: '', category_id: '', cost_center_id: '',
      competence_date: '', invoice_date: '', invoice_number: '', due_date: '', 
      forecast_date: '', // Data de previsão
      amount: '', payment_method: '', notes: '',
      bank_account_id: '', // Conta bancária de destino
      is_installment: false, total_amount: '', num_installments: '', first_due_date: '',
      is_recurring: false, recurrence_type: 'monthly', recurrence_duration: 'indefinite', recurrence_count: '12',
      // Flag antecipação
      is_antecipacao: false,
      // Tax retention fields
      has_retention: false, retention_type: 'ir', retention_rate: '', retention_amount: ''
    });
  };

  const handleCreateCategory = async () => {
    if (!newCategoryName.trim()) return;
    try {
      const res = await axios.post(`${API_URL}/api/finance/categories`, {
        name: newCategoryName,
        type: 'expense'
      }, { headers });
      toast.success('Categoria criada!');
      setNewCategoryName('');
      setIsCreateCategoryOpen(false);
      fetchCategories();
      // Auto-select the new category
      if (res.data.id) {
        setFormData(prev => ({ ...prev, category_id: res.data.id }));
      }
    } catch (error) {
      toast.error('Erro ao criar categoria');
    }
  };

  const handleCreateVendor = async () => {
    if (!newVendorData.name.trim()) return;
    try {
      const res = await axios.post(`${API_URL}/api/finance/vendors`, newVendorData, { headers });
      toast.success('Fornecedor criado!');
      setNewVendorData({ name: '', document: '', phone: '', email: '' });
      setIsCreateVendorOpen(false);
      fetchVendors();
      // Auto-select the new vendor
      if (res.data.name) {
        setFormData(prev => ({ ...prev, vendor_name: res.data.name, vendor_id: res.data.id }));
      }
    } catch (error) {
      toast.error('Erro ao criar fornecedor');
    }
  };

  const handleCreateCostCenter = async () => {
    if (!newCostCenterData.name.trim() || !newCostCenterData.code.trim()) return;
    try {
      const res = await axios.post(`${API_URL}/api/finance/cost-centers`, newCostCenterData, { headers });
      toast.success('Centro de custo criado!');
      setNewCostCenterData({ code: '', name: '' });
      setIsCreateCostCenterOpen(false);
      fetchCostCenters();
      // Auto-select the new cost center
      if (res.data.id) {
        setFormData(prev => ({ ...prev, cost_center_id: res.data.id }));
      }
    } catch (error) {
      toast.error('Erro ao criar centro de custo');
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    
    // Validação obrigatória: cost_center_id
    if (!formData.cost_center_id) {
      toast.error('Centro de Custo é obrigatório');
      return;
    }
    
    setIsSubmitting(true);
    try {
      let payload;
      if (formData.is_installment) {
        payload = {
          description: formData.description,
          vendor_name: formData.vendor_name,
          vendor_id: formData.vendor_id || null,
          category_id: formData.category_id,
          cost_center_id: formData.cost_center_id,
          payment_method: formData.payment_method,
          bank_account_id: formData.bank_account_id || null,
          notes: formData.notes,
          competence_date: formData.competence_date || null,
          invoice_date: formData.invoice_date || null,
          invoice_number: formData.invoice_number || null,
          is_installment: true,
          total_amount: parseFloat(formData.total_amount),
          num_installments: parseInt(formData.num_installments),
          first_due_date: formData.first_due_date,
          amount: 0, due_date: formData.first_due_date,
          is_antecipacao: formData.is_antecipacao || false
        };
      } else if (formData.is_recurring) {
        payload = {
          description: formData.description,
          vendor_name: formData.vendor_name,
          vendor_id: formData.vendor_id || null,
          category_id: formData.category_id,
          cost_center_id: formData.cost_center_id,
          payment_method: formData.payment_method,
          bank_account_id: formData.bank_account_id || null,
          notes: formData.notes,
          competence_date: formData.competence_date || null,
          invoice_date: formData.invoice_date || null,
          invoice_number: formData.invoice_number || null,
          nf_number: formData.nf_number || null,
          is_recurring: true,
          recurrence_type: formData.recurrence_type,
          recurrence_duration: formData.recurrence_duration,
          recurrence_count: formData.recurrence_duration === 'fixed' ? parseInt(formData.recurrence_count) : null,
          due_date: formData.due_date,
          forecast_date: formData.forecast_date || null,
          amount: parseFloat(formData.amount),
          is_antecipacao: formData.is_antecipacao || false
        };
      } else {
        payload = {
          description: formData.description,
          vendor_name: formData.vendor_name,
          vendor_id: formData.vendor_id || null,
          category_id: formData.category_id,
          cost_center_id: formData.cost_center_id,
          payment_method: formData.payment_method,
          bank_account_id: formData.bank_account_id || null,
          notes: formData.notes,
          competence_date: formData.competence_date || null,
          invoice_date: formData.invoice_date || null,
          invoice_number: formData.invoice_number || null,
          nf_number: formData.nf_number || null,
          due_date: formData.due_date,
          forecast_date: formData.forecast_date || null,
          amount: parseFloat(formData.amount),
          is_antecipacao: formData.is_antecipacao || false,
          // Tax retention fields
          has_retention: formData.has_retention || false,
          retention_type: formData.has_retention ? formData.retention_type : null,
          retention_rate: formData.has_retention ? parseFloat(formData.retention_rate) || null : null,
          retention_amount: formData.has_retention ? parseFloat(formData.retention_amount) || null : null
        };
      }

      const res = await axios.post(`${API_URL}/api/finance/payables`, payload, { headers });
      const count = res.data.count || 1;
      if (formData.is_installment) {
        toast.success(`${count} parcelas criadas!`);
      } else if (formData.is_recurring) {
        toast.success(`${count} lançamentos recorrentes criados!`);
      } else {
        toast.success('Conta a pagar criada!');
      }
      setIsCreateOpen(false);
      resetFormData();
      fetchPayables();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao criar conta');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEdit = async (e) => {
    e.preventDefault();
    if (!selectedPayable) return;
    setIsSubmitting(true);
    try {
      // Clean up form data - convert types
      const payload = {};
      Object.keys(editFormData).forEach(key => {
        const value = editFormData[key];
        if (value !== undefined) {
          // Convert amount to float
          if (key === 'amount') {
            if (value !== '' && value !== null) {
              payload[key] = parseFloat(value);
            }
          } else if (['forecast_date', 'competence_date', 'invoice_date', 'payment_date'].includes(key)) {
            // Date fields: send null if empty, otherwise send value
            payload[key] = value || null;
          } else if (value !== '' && value !== null) {
            payload[key] = value;
          }
        }
      });
      
      await axios.put(`${API_URL}/api/finance/payables/${selectedPayable.id}`, payload, { headers });
      
      // Update local state immediately for better UX
      setPayables(prev => prev.map(p => 
        p.id === selectedPayable.id 
          ? { ...p, ...payload } 
          : p
      ));
      
      toast.success('Conta atualizada!');
      setIsEditOpen(false);
      setSelectedPayable(null);
      
      // Also refetch for consistency
      fetchPayables();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao atualizar conta');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInstallmentEdit = async () => {
    if (!selectedPayable) return;
    setIsSubmitting(true);
    try {
      const payload = { ...installmentEditData, payable_id: selectedPayable.id };
      // Remove empty strings
      Object.keys(payload).forEach(k => { if (payload[k] === '') delete payload[k]; });
      if (payload.amount) payload.amount = parseFloat(payload.amount);

      await axios.patch(`${API_URL}/api/finance/installments/${selectedPayable.installment_group_id}`, payload, { headers });
      toast.success('Parcelas atualizadas!');
      setIsInstallmentEditOpen(false);
      setSelectedPayable(null);
      fetchPayables();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao atualizar parcelas');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handlePay = async () => {
    if (!selectedPayable) return;

    // VALIDAÇÃO (e): Categoria "Aguardando..." em status Pago — avisa antes de pagar
    const cat = (selectedPayable.category || '').toLowerCase();
    if (cat.includes('aguardando') || cat.includes('aguard')) {
      const ok = window.confirm(
        `⚠ ATENÇÃO: Esta conta está com categoria "${selectedPayable.category}" — uma categoria de triagem.\n\n` +
        `Antes de marcar como Pago, é recomendado reclassificar pra uma categoria final (ex: RH, Impostos, Fornecedor, etc).\n\n` +
        `Continuar mesmo assim?`
      );
      if (!ok) return;
    }
    
    // Re-fetch the payable to get the latest status (prevents stale state issues after replicate)
    try {
      const freshRes = await axios.get(`${API_URL}/api/finance/payables/${selectedPayable.id}`, { headers });
      const freshPayable = freshRes.data;
      if (freshPayable.status === 'paid') {
        toast.error('Esta conta já está paga. A lista foi atualizada.');
        setIsPayOpen(false);
        setSelectedPayable(null);
        fetchPayables();
        return;
      }
    } catch (e) {
      toast.error('Erro ao verificar status da conta. Tente novamente.');
      setIsPayOpen(false);
      return;
    }
    
    setIsSubmitting(true);
    try {
      const form = new FormData();
      form.append('payment_date', payFormData.paid_date);
      form.append('payment_method', selectedPayable.payment_method || 'transfer');
      if (payFormData.bank_account_id) form.append('bank_account_id', payFormData.bank_account_id);
      
      // Multa, Juros e Desconto
      // Only send paid_amount if there are no penalty/interest (let backend calculate total with fees)
      const hasFees = payFormData.penalty_amount || payFormData.interest_amount || payFormData.discount_amount;
      if (!hasFees && payFormData.paid_amount) {
        form.append('paid_amount', payFormData.paid_amount);
      }
      if (payFormData.penalty_amount) form.append('penalty_amount', payFormData.penalty_amount);
      if (payFormData.interest_amount) form.append('interest_amount', payFormData.interest_amount);
      if (payFormData.discount_amount) form.append('discount_amount', payFormData.discount_amount);
      if (payFormData.notes) form.append('notes', payFormData.notes);
      
      // Opção para não criar transação bancária automática
      form.append('skip_bank_transaction', payFormData.skip_bank_transaction ? 'true' : 'false');

      await axios.post(`${API_URL}/api/finance/payables/${selectedPayable.id}/pay`, form, { headers });
      toast.success('Pagamento registrado!');
      setIsPayOpen(false);
      setSelectedPayable(null);
      fetchPayables();
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Erro ao registrar pagamento';
      toast.error(errorMsg);
      // If already paid error, refresh the list
      if (errorMsg.toLowerCase().includes('já') && errorMsg.toLowerCase().includes('pag')) {
        fetchPayables();
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  // Estornar (reverter) pagamento
  const [isReverseOpen, setIsReverseOpen] = useState(false);
  
  const handleReverse = async () => {
    if (!selectedPayable) return;
    setIsSubmitting(true);
    try {
      await axios.post(`${API_URL}/api/finance/payables/${selectedPayable.id}/reverse`, {}, { headers });
      toast.success('Pagamento estornado! A conta voltou ao status Pendente.');
      setIsReverseOpen(false);
      setSelectedPayable(null);
      fetchPayables();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao estornar pagamento');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedPayable) return;
    setIsSubmitting(true);
    try {
      const params = new URLSearchParams();
      const hasGroup = selectedPayable.recurring_group_id || selectedPayable.installment_group_id;
      
      if (hasGroup) {
        if (deleteRecurringFuture) {
          params.append('delete_future', 'true');
        }
        if (deleteRecurringPast) {
          params.append('delete_past', 'true');
        }
      }
      
      // Captura o id ANTES de zerar selectedPayable (a UI fechará logo após)
      const deletedId = selectedPayable.id;
      const deletedGroupId = selectedPayable.installment_group_id || selectedPayable.recurring_group_id || null;
      const wasGroupDelete = hasGroup && (deleteRecurringFuture || deleteRecurringPast);

      await axios.delete(`${API_URL}/api/finance/payables/${deletedId}?${params.toString()}`, { headers });
      
      let message = 'Conta excluída com sucesso!';
      if (deleteRecurringFuture && deleteRecurringPast) {
        message = 'Todas as contas não pagas do grupo excluídas!';
      } else if (deleteRecurringFuture) {
        message = 'Conta e próximas não pagas excluídas!';
      } else if (deleteRecurringPast) {
        message = 'Conta e anteriores não pagas excluídas!';
      }
      
      toast.success(message);
      setIsDeleteOpen(false);
      setSelectedPayable(null);
      setDeleteRecurringFuture(false);
      setDeleteRecurringPast(false);

      // Remoção OTIMISTA local — evita "flash" da lista inteira sumindo durante o refetch.
      // 1) Se foi exclusão única: remove só aquele id.
      // 2) Se foi exclusão de grupo (futuras/passadas): remove todos os não-pagos do mesmo grupo.
      setPayables(prev => prev.filter(p => {
        if (p.id === deletedId) return false;
        if (wasGroupDelete && deletedGroupId) {
          const sameGroup = (p.installment_group_id === deletedGroupId) || (p.recurring_group_id === deletedGroupId);
          if (!sameGroup) return true;
          // não toca em pagos
          if (p.status === 'paid' || p.status === 'partially_paid') return true;
          // remove só os que se encaixam na regra escolhida (futuras / passadas / ambas)
          try {
            const delDate = new Date(selectedPayable?.due_date || 0).getTime();
            const pDate = new Date(p.due_date || 0).getTime();
            if (deleteRecurringFuture && deleteRecurringPast) return false;
            if (deleteRecurringFuture && pDate >= delDate) return false;
            if (deleteRecurringPast && pDate <= delDate) return false;
            return true;
          } catch {
            return true;
          }
        }
        return true;
      }));
      // Refresh "silencioso" em background pra sincronizar summary/totais do servidor,
      // sem mostrar skeleton (a lista já está localmente coerente).
      // Pequeno delay garante que o backend já persistiu antes de re-listar.
      setTimeout(() => fetchPayables({ silent: true }), 150);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao excluir conta');
    } finally {
      setIsSubmitting(false);
    }
  };

  const openDeleteDialog = (payable) => {
    setSelectedPayable(payable);
    setDeleteRecurringFuture(false);
    setDeleteRecurringPast(false);
    setIsDeleteOpen(true);
  };

  // Multi-select functions
  const toggleSelectItem = (payableId) => {
    setSelectedItems(prev => 
      prev.includes(payableId) 
        ? prev.filter(id => id !== payableId)
        : [...prev, payableId]
    );
  };

  const toggleSelectAll = () => {
    // Get all payable IDs from filtered list
    const allIds = filteredPayables.flatMap(([_, items]) => items.map(p => p.id));
    if (selectedItems.length === allIds.length) {
      setSelectedItems([]);
    } else {
      setSelectedItems(allIds);
    }
  };

  const getSelectedTotal = () => {
    // Soma usando o mesmo campo que a coluna "Valor" exibe (net_amount || amount),
    // garantindo que a barra "Total" SEMPRE bata com a soma visual das linhas
    // selecionadas. Sem isso, registros antigos com `amount` e `net_amount`
    // dessincronizados (bug do parcelamento legado) faziam a barra divergir.
    // Para parcialmente pagos, usa paid_amount (o que realmente saiu).
    return payables
      .filter(p => selectedItems.includes(p.id))
      .reduce((sum, p) => {
        if (p.status === 'partially_paid') {
          return sum + (parseFloat(p.paid_amount) || 0);
        }
        const v = (p.net_amount !== undefined && p.net_amount !== null)
          ? p.net_amount
          : p.amount;
        return sum + (parseFloat(v) || 0);
      }, 0);
  };

  const getSelectedCount = () => selectedItems.length;

  const handleBulkDelete = async () => {
    if (selectedItems.length === 0) return;
    setIsSubmitting(true);
    try {
      // Delete each selected item
      await Promise.all(
        selectedItems.map(id => 
          axios.delete(`${API_URL}/api/finance/payables/${id}`, { headers })
        )
      );
      toast.success(`${selectedItems.length} conta(s) excluída(s) com sucesso!`);
      setIsBulkDeleteOpen(false);
      setSelectedItems([]);
      fetchPayables();
    } catch (error) {
      toast.error('Erro ao excluir contas selecionadas');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleBulkEdit = async () => {
    if (selectedItems.length === 0) return;
    setIsSubmitting(true);
    try {
      // Filter out empty values
      const updateData = {};
      if (bulkEditData.category_id) updateData.category_id = bulkEditData.category_id;
      if (bulkEditData.cost_center_id) updateData.cost_center_id = bulkEditData.cost_center_id;
      if (bulkEditData.forecast_date) updateData.forecast_date = bulkEditData.forecast_date;
      if (bulkEditData.competence_date) updateData.competence_date = bulkEditData.competence_date;
      if (bulkEditData.notes) updateData.notes = bulkEditData.notes;
      if (bulkEditData.amount) updateData.amount = parseFloat(bulkEditData.amount);

      if (Object.keys(updateData).length === 0) {
        toast.error('Selecione pelo menos um campo para atualizar');
        return;
      }

      await axios.post(`${API_URL}/api/finance/payables/bulk`, {
        ids: selectedItems,
        action: 'update',
        update_data: updateData
      }, { headers });

      toast.success(`${selectedItems.length} conta(s) atualizada(s) com sucesso!`);
      setIsBulkEditOpen(false);
      setSelectedItems([]);
      setBulkEditData({
        category_id: '',
        cost_center_id: '',
        forecast_date: '',
        competence_date: '',
        notes: '',
        amount: ''
      });
      fetchPayables();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao atualizar contas selecionadas');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleBulkPay = async () => {
    if (!bulkPayData.bank_account_id) {
      toast.error('Selecione uma conta bancária');
      return;
    }
    setIsSubmitting(true);
    try {
      const response = await axios.post(`${API_URL}/api/finance/payables/bulk`, {
        ids: selectedItems,
        action: 'pay',
        update_data: bulkPayData
      }, { headers });
      toast.success(response.data.message || `${selectedItems.length} conta(s) paga(s) com sucesso!`);
      setIsBulkPayOpen(false);
      setSelectedItems([]);
      setBulkPayData({
        bank_account_id: '',
        payment_date: new Date().toISOString().split('T')[0],
        payment_method: 'transfer'
      });
      fetchPayables();
      fetchBankAccounts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao pagar contas selecionadas');
    } finally {
      setIsSubmitting(false);
    }
  };

  const openReplicateDialog = (payable) => {
    setSelectedPayable(payable);
    // Default to next month's due date - use parseLocalDate to avoid timezone issues
    const originalDate = parseLocalDate(payable.due_date);
    originalDate.setMonth(originalDate.getMonth() + 1);
    setReplicateDate(originalDate.toISOString().split('T')[0]);
    setReplicateAmount(payable.amount?.toString() || '');
    setReplicateDescription(payable.description || '');
    setIsReplicateOpen(true);
  };

  const handleReplicate = async () => {
    if (!selectedPayable || !replicateDate) return;
    setIsSubmitting(true);
    try {
      const payload = { new_due_date: replicateDate };
      // Always send amount from the form
      const parsedAmount = parseFloat(replicateAmount);
      if (!isNaN(parsedAmount)) {
        payload.amount = parsedAmount;
      }
      // Always send description from the form
      if (replicateDescription) {
        payload.description = replicateDescription;
      }
      await axios.post(`${API_URL}/api/finance/payables/${selectedPayable.id}/replicate`, 
        payload, 
        { headers }
      );
      toast.success('Conta replicada com sucesso!');
      setIsReplicateOpen(false);
      setSelectedPayable(null);
      setReplicateDate('');
      setReplicateAmount('');
      setReplicateDescription('');
      fetchPayables();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao replicar conta');
    } finally {
      setIsSubmitting(false);
    }
  };

  // ====== Parcelar saldo (negociação) ======
  // Quando o dialog abre, inicializa com 2 parcelas iguais e data = vencimento atual.
  useEffect(() => {
    if (!isParcelOpen || !selectedPayable) return;
    const total = parseFloat(selectedPayable.amount || 0);
    setParcelCount(2);
    setParcelFirstDate(selectedPayable.due_date || new Date().toISOString().slice(0, 10));
    setParcelUseEqual(true);
    const each = Math.round((total / 2) * 100) / 100;
    setParcelAmounts([each, Math.round((total - each) * 100) / 100]);
  }, [isParcelOpen, selectedPayable]);

  // Recalcular valores quando muda quantidade ou modo igual
  useEffect(() => {
    if (!isParcelOpen || !selectedPayable) return;
    if (!parcelUseEqual) return;
    const total = parseFloat(selectedPayable.amount || 0);
    const n = Math.max(2, parcelCount);
    const base = Math.round((total / n) * 100) / 100;
    const arr = Array.from({ length: n }, (_, i) =>
      i < n - 1 ? base : Math.round((total - base * (n - 1)) * 100) / 100
    );
    setParcelAmounts(arr);
  }, [parcelCount, parcelUseEqual, isParcelOpen, selectedPayable]);

  const parcelSum = parcelAmounts.reduce((s, v) => s + (parseFloat(v) || 0), 0);
  const parcelOriginalAmount = parseFloat(selectedPayable?.amount || 0);
  const parcelDiff = Math.round((parcelSum - parcelOriginalAmount) * 100) / 100;

  const handleParcel = async () => {
    if (!selectedPayable || !parcelFirstDate) {
      toast.error('Defina a data da primeira parcela');
      return;
    }
    if (Math.abs(parcelDiff) > 0.01) {
      toast.error(`Soma das parcelas (${formatCurrency(parcelSum)}) não bate com o total (${formatCurrency(parcelOriginalAmount)})`);
      return;
    }
    setIsSubmitting(true);
    try {
      const payload = {
        num_installments: parcelCount,
        first_due_date: parcelFirstDate,
      };
      if (!parcelUseEqual) {
        payload.amounts = parcelAmounts.map(v => parseFloat(v) || 0);
      }
      await axios.post(
        `${API_URL}/api/finance/payables/${selectedPayable.id}/parcel`,
        payload,
        { headers }
      );
      toast.success(`Conta parcelada em ${parcelCount}x!`);
      setIsParcelOpen(false);
      setSelectedPayable(null);
      fetchPayables();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao parcelar');
    } finally {
      setIsSubmitting(false);
    }
  };

  const openViewDialog = (payable) => {
    setSelectedPayable(payable);
    setIsViewOpen(true);
  };

  const openEditDialog = (payable) => {
    setSelectedPayable(payable);
    // Normalize ISO datetime -> YYYY-MM-DD (input[type=date] rejects time component)
    const toDateOnly = (d) => {
      if (!d) return '';
      if (typeof d === 'string') return d.slice(0, 10);
      try { return new Date(d).toISOString().slice(0, 10); } catch { return ''; }
    };
    setEditFormData({
      vendor_name: payable.vendor_name || '',
      description: payable.description || '',
      category_id: payable.category_id || '',
      cost_center_id: payable.cost_center_id || '',
      payment_method: payable.payment_method || '',
      due_date: toDateOnly(payable.due_date),
      forecast_date: toDateOnly(payable.forecast_date),
      amount: payable.amount?.toString() || '',
      notes: payable.notes || '',
      invoice_number: payable.invoice_number || '',
      invoice_date: toDateOnly(payable.invoice_date),
      competence_date: toDateOnly(payable.competence_date),
      // Payment fields (for paid accounts)
      payment_date: toDateOnly(payable.payment_date),
      bank_account_id: payable.bank_account_id || '',
      is_antecipacao: payable.is_antecipacao || false
    });
    
    if (payable.installment_group_id) {
      setInstallmentEditData({
        scope: 'single',
        description: '', 
        vendor_name: '', 
        category_id: '',
        category: '',
        cost_center_id: '', 
        payment_method: '', 
        amount: '', 
        notes: '',
        bank_account_id: '',
        invoice_number: '',
        invoice_date: '',
        competence_date: '',
        due_date: '',
        forecast_date: ''
      });
      setIsInstallmentEditOpen(true);
    } else {
      setIsEditOpen(true);
    }
  };

  const openPayDialog = (payable) => {
    setSelectedPayable(payable);
    setPayFormData({
      paid_amount: '',  // Start empty - let backend compute from amount + fees. User can override if needed.
      paid_date: new Date().toISOString().split('T')[0],
      bank_account_id: '',
      penalty_amount: '',
      interest_amount: '',
      discount_amount: '',
      notes: ''
    });
    setIsPayOpen(true);
  };

  const toggleGroup = (groupId) => {
    setExpandedGroups(prev => ({ ...prev, [groupId]: !prev[groupId] }));
  };

  // Group payables by installment_group_id (only if groupInstallments is enabled)
  const groupedPayables = payables.reduce((acc, p) => {
    if (groupInstallments && p.installment_group_id) {
      // Agrupamento habilitado: agrupa por installment_group_id
      if (!acc[p.installment_group_id]) acc[p.installment_group_id] = [];
      acc[p.installment_group_id].push(p);
    } else {
      // Agrupamento desabilitado ou conta sem grupo: cada conta em linha individual
      acc[`single_${p.id}`] = [p];
    }
    return acc;
  }, {});

  // Sorting function
  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  // Sort items - handle special cases for category and dates
  const sortItems = useCallback((items) => {
    return [...items].sort((a, b) => {
      let aVal = a[sortConfig.key];
      let bVal = b[sortConfig.key];
      
      // Handle category - it's a string field
      if (sortConfig.key === 'category') {
        aVal = a.category || '';
        bVal = b.category || '';
      }
      
      // Handle nulls
      if (aVal === null || aVal === undefined) aVal = '';
      if (bVal === null || bVal === undefined) bVal = '';
      
      // Handle numbers
      if (sortConfig.key === 'amount' || sortConfig.key === 'paid_amount') {
        aVal = parseFloat(aVal) || 0;
        bVal = parseFloat(bVal) || 0;
      }
      
      // Handle dates - convert to comparable format
      if (sortConfig.key.includes('date') && aVal && bVal) {
        aVal = new Date(aVal).getTime() || 0;
        bVal = new Date(bVal).getTime() || 0;
      }
      
      // Handle strings
      if (typeof aVal === 'string') aVal = aVal.toLowerCase();
      if (typeof bVal === 'string') bVal = bVal.toLowerCase();
      
      if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
  }, [sortConfig]);

  // Helper function to check if item matches search term
  const matchesSearch = (p, term) => {
    const lowerTerm = term.toLowerCase();
    return (
      p.description?.toLowerCase().includes(lowerTerm) ||
      p.vendor_name?.toLowerCase().includes(lowerTerm) ||
      p.category?.toLowerCase().includes(lowerTerm) ||
      p.cost_center?.toLowerCase().includes(lowerTerm) ||
      p.invoice_number?.toLowerCase().includes(lowerTerm) ||
      p.notes?.toLowerCase().includes(lowerTerm) ||
      p.bank_account_name?.toLowerCase().includes(lowerTerm)
    );
  };

  // Check if any filter is active
  const hasActiveFilters = searchTerm
    || Object.keys(advancedFilters).some(k => advancedFilters[k]?.value)
    || filterStatus !== 'all'
    || vendorFilter !== 'all'
    || categoryFilter !== 'all'
    || costCenterFilter !== 'all'
    || !!quickPreset;

  // Apply all filters
  const filteredPayables = useMemo(() => {
    let result = Object.entries(groupedPayables);

    // Apply search term filter
    if (searchTerm) {
      result = result.filter(([key, items]) => items.some(p => matchesSearch(p, searchTerm)));
      result = result.map(([key, items]) => [key, items.filter(p => matchesSearch(p, searchTerm))]);
    }

    // Apply advanced filters
    if (Object.keys(advancedFilters).some(k => advancedFilters[k]?.value)) {
      result = result.map(([key, items]) => {
        const filtered = applyAdvancedFilters(items, advancedFilters, dateColumns);
        return [key, filtered];
      }).filter(([key, items]) => items.length > 0);
    }

    // Apply quick filters (vendor, category, cost_center) + quickPreset client-side
    if (vendorFilter !== 'all' || categoryFilter !== 'all' || costCenterFilter !== 'all' || quickPreset) {
      result = result.map(([key, items]) => {
        const filtered = applyLedgerClientFilters(items, {
          vendorFilter, categoryFilter, costCenterFilter, quickPreset,
          isOverdueFn: isOverdue,
        });
        return [key, filtered];
      }).filter(([key, items]) => items.length > 0);
    }

    // Apply sorting
    // 1) ordena os itens DENTRO de cada grupo de parcelas
    // 2) ordena os GRUPOS entre si pelo "menor valor" da chave de ordenação
    //    (para asc -> menor primeiro; para desc -> maior primeiro). Sem isso,
    //    clicar para ordenar por Vencimento ascendente não reorganizava os
    //    grupos — só os itens internos.
    const sortedWithinGroups = result.map(([key, items]) => [key, sortItems(items)]);
    const getGroupSortValue = (items) => {
      if (!items || items.length === 0) return 0;
      const k = sortConfig.key;
      // Convert each item's key to a comparable scalar
      const toScalar = (it) => {
        let v = it?.[k];
        if (v === null || v === undefined) v = '';
        if (k && k.includes && k.includes('date') && v) {
          const n = new Date(v).getTime();
          return Number.isFinite(n) ? n : 0;
        }
        if (k === 'amount' || k === 'paid_amount') {
          return parseFloat(v) || 0;
        }
        return typeof v === 'string' ? v.toLowerCase() : v;
      };
      const vals = items.map(toScalar);
      // For asc: representative is MIN (oldest/lowest), for desc: MAX
      return sortConfig.direction === 'asc' ? vals.reduce((a, b) => (a < b ? a : b)) : vals.reduce((a, b) => (a > b ? a : b));
    };
    sortedWithinGroups.sort(([, aItems], [, bItems]) => {
      const av = getGroupSortValue(aItems);
      const bv = getGroupSortValue(bItems);
      if (av < bv) return sortConfig.direction === 'asc' ? -1 : 1;
      if (av > bv) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
    return sortedWithinGroups;
  }, [groupedPayables, searchTerm, advancedFilters, sortItems, sortConfig, vendorFilter, categoryFilter, costCenterFilter, quickPreset]);

  // Calculate filtered summary when any filter is active
  const filteredSummary = hasActiveFilters ? (() => {
    const allFilteredItems = filteredPayables.flatMap(([_, items]) => items);
    const pending = allFilteredItems.filter(p => p.status === 'pending');
    const paid = allFilteredItems.filter(p => p.status === 'paid' || p.status === 'partially_paid');
    const overdue = allFilteredItems.filter(p => p.status === 'pending' && isOverdue(p.due_date));
    // Helpers:
    //  - "saldo aberto" = max(0, gross - paid)  → usado em Em Aberto / Vencidos
    //  - "valor pago"   = paid_amount          → usado no card "Pago"
    //  - isEffectivelyPartial: detecta parcial real mesmo quando status veio como "paid"
    //    (caso de dado legado/manual com paid_amount<amount ou residual vinculado).
    //  - effectiveAmount: para parc.pago a "amount real" da linha é o que efetivamente
    //    saiu do caixa (paid_amount); o restante vive em uma linha "Saldo Residual"
    //    separada. Sem isso o total geral somava duas vezes o resíduo (parent.amount
    //    inclui tudo + child.amount inclui o pendente, dobra contagem).
    const openBalance = (p) => Math.max(0, (parseFloat(p.amount) || 0) - (parseFloat(p.paid_amount) || 0));
    const paidValue   = (p) => parseFloat(p.paid_amount) || 0;
    const isEffectivelyPartial = (p) => {
      if (p.status === 'partially_paid') return true;
      if (p.status === 'paid') {
        if (p.remaining_payable_id || p.is_partial_payment) return true;
        const amt = parseFloat(p.amount) || 0;
        const pd = parseFloat(p.paid_amount) || 0;
        if (pd > 0 && amt - pd > 0.01) return true;
      }
      return false;
    };
    const effectiveAmount = (p) => isEffectivelyPartial(p)
      ? paidValue(p)
      : (parseFloat(p.amount) || 0);
    return {
      pending: { total: pending.reduce((s, p) => s + openBalance(p), 0), count: pending.length },
      paid:    { total: paid.reduce((s, p) => s + paidValue(p), 0),       count: paid.length },
      overdue: { total: overdue.reduce((s, p) => s + openBalance(p), 0), count: overdue.length },
      total:   allFilteredItems.reduce((s, p) => s + effectiveAmount(p), 0),
      count:   allFilteredItems.length
    };
  })() : null;

  const summaryTotal = Object.values(payablesSummary).reduce((acc, s) => acc + (s.total || 0), 0);
  
  // Use filtered summary when any filter is active
  const displaySummary = filteredSummary || payablesSummary;
  const displayTotal = filteredSummary ? filteredSummary.total : summaryTotal;

  // Build period label + handler for PDF export (shared between validator & export-anyway)
  const buildPeriodLabel = () => {
    if (filterMonthRange === -1) {
      const start = customStartDate ? customStartDate.split('-').reverse().join('/') : '?';
      const end = customEndDate ? customEndDate.split('-').reverse().join('/') : '?';
      return `${start} a ${end}`;
    } else if (filterMonthRange === 0) {
      return 'Todos os períodos';
    } else if (filterMonthRange === 1) {
      return formatMonth(filterMonth);
    } else {
      const [y, m] = filterMonth.split('-').map(n => parseInt(n));
      const startDate = new Date(y, m - 1, 1);
      const endDate = new Date(y, m - 1 + filterMonthRange - 1, 1);
      const months = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];
      if (startDate.getFullYear() === endDate.getFullYear()) {
        return `${months[startDate.getMonth()]} a ${months[endDate.getMonth()]}/${endDate.getFullYear()}`;
      } else {
        return `${months[startDate.getMonth()]}/${startDate.getFullYear()} a ${months[endDate.getMonth()]}/${endDate.getFullYear()}`;
      }
    }
  };

  const doExportPdf = () => {
    const allPayables = filteredPayables.flatMap(([_, items]) => items);
    if (allPayables.length === 0) {
      toast.warning('Nenhum lançamento para gerar PDF.');
      return;
    }
    const advancedDesc = Object.entries(advancedFilters || {})
      .filter(([, v]) => v?.value)
      .map(([k, v]) => `${k}: ${v.value}`)
      .join('; ');
    const totals = displaySummary || {};
    const summary = {
      pending: totals.pending || { total: 0, count: 0 },
      paid: totals.paid || { total: 0, count: 0 },
      overdue: totals.overdue || { total: 0, count: 0 },
      total: displayTotal || 0,
      count: allPayables.length,
    };
    try {
      // Resolve a single selected account so PDF header reflects the right company
      const selectedAccount = (!allSelected && Array.isArray(selectedAccountIds) && selectedAccountIds.length === 1)
        ? (bankAccounts || []).find(a => a.id === selectedAccountIds[0] || a._id === selectedAccountIds[0])
        : null;
      generateLedgerPdf({
        type: 'payables',
        items: allPayables,
        summary,
        filters: {
          period_label: buildPeriodLabel(),
          status: filterStatus,
          installment: filterInstallment,
          date_field: dateFilterField,
          search: searchTerm,
          advanced: advancedDesc,
        },
        tenant,
        user,
        selectedAccount,
      });
      toast.success('PDF gerado com sucesso');
    } catch (e) {
      toast.error('Erro ao gerar PDF');
    }
  };

  return (
    <div className="space-y-4" data-testid="payables-page">
      <ValidateExportDialog
        open={exportValidatorOpen}
        onOpenChange={setExportValidatorOpen}
        kind="payable"
        startDate={filterMonthRange === -1 ? customStartDate : null}
        endDate={filterMonthRange === -1 ? customEndDate : null}
        dateField={dateFilterField}
        onExportAnyway={doExportPdf}
      />
      {/* Summary Cards CLICÁVEIS */}
      <ClickableSummaryCards
        summary={{
          pending: displaySummary.pending,
          paid: displaySummary.paid,
          overdue: displaySummary.overdue,
          total: displayTotal,
          count: filteredSummary?.count || Object.values(payablesSummary).reduce((s, x) => s + (x.count || 0), 0),
        }}
        statusActive={filterStatus}
        onStatusChange={(target) => {
          if (target === '__overdue__') {
            setQuickPreset(quickPreset === 'overdue' ? null : 'overdue');
            setFilterStatus('unpaid');
            setFilterMonthRange(0);
          } else {
            setQuickPreset(null);
            setFilterStatus(target);
          }
        }}
        variant="payables"
        hasActiveFilters={hasActiveFilters}
      />

      {/* Atalhos rápidos (presets) */}
      <QuickPresets
        preset={quickPreset}
        onApply={(p) => {
          const today = new Date();
          const ym = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}`;
          setQuickPreset(p);
          if (p === 'overdue' || p === 'all_open' || p === 'overdue_today') {
            setFilterStatus('unpaid');
            setFilterMonthRange(0);
            setDateFilterField('due_date');
          } else if (p === 'today' || p === 'this_week') {
            setFilterStatus('unpaid');
            setFilterMonth(ym);
            setFilterMonthRange(1);
            setDateFilterField('due_date');
          } else if (p === 'paid_this_month') {
            setFilterStatus('paid');
            setFilterMonth(ym);
            setFilterMonthRange(1);
            setDateFilterField('payment_date');
          } else if (p === null) {
            setFilterStatus('all');
          }
        }}
        variant="payables"
      />

      {/* ===== TOOLBAR REORGANIZADA ===== */}
      <div className="space-y-2">
        {/* Linha 1: Filtros */}
        <div className="flex flex-wrap gap-2 items-center">
          <MonthNavigator
            filterMonth={filterMonth}
            setFilterMonth={setFilterMonth}
            filterMonthRange={filterMonthRange}
            setFilterMonthRange={setFilterMonthRange}
            formatMonth={formatMonth}
            changeMonth={changeMonth}
            dateFilterField={dateFilterField}
            setDateFilterField={setDateFilterField}
            dateFieldOptions={dateFieldOptions}
            customStartDate={customStartDate}
            setCustomStartDate={setCustomStartDate}
            customEndDate={customEndDate}
            setCustomEndDate={setCustomEndDate}
          />

          <SearchInput
            value={searchTerm}
            onChange={setSearchTerm}
            placeholder="Buscar por descrição, fornecedor, NF..."
            testId="search-payables"
          />

          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger className="h-9 w-[140px]" data-testid="filter-status">
              <Filter className="h-3.5 w-3.5 mr-1.5" /><SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos status</SelectItem>
              <SelectItem value="unpaid">Em aberto</SelectItem>
              <SelectItem value="pending">Pendente</SelectItem>
              <SelectItem value="paid">Pago</SelectItem>
              <SelectItem value="partially_paid">Parc. Pago</SelectItem>
              <SelectItem value="cancelled">Cancelado</SelectItem>
            </SelectContent>
          </Select>

          <MoreFiltersButton
            filterInstallment={filterInstallment}
            setFilterInstallment={setFilterInstallment}
            groupInstallments={groupInstallments}
            setGroupInstallments={setGroupInstallments}
          />

          <AdvancedFilters
            columns={filterableColumns}
            filters={advancedFilters}
            onFilterChange={setAdvancedFilters}
            onClearFilters={() => setAdvancedFilters({})}
            dateColumns={dateColumns}
          />

          {/* Ações no final da Linha 1 — alinhadas à direita */}
          <div className="ml-auto flex gap-2 items-center">
            <ColumnSelector
              columns={PAYABLE_COLUMNS}
              visibleColumns={visibleColumns}
              onChange={setVisibleColumns}
              storageKey="payables_visible_columns"
            />
            <ExportDropdown
              onExportExcel={() => {
                const allPayables = filteredPayables.flatMap(([_, items]) => items);
                exportToExcel(allPayables, EXPORT_CONFIGS.payables, `contas-a-pagar-${filterMonth}`, 'Contas a Pagar');
              }}
              onExportPdf={() => setExportValidatorOpen(true)}
            />

            {user?.role === 'admin' && (
              <AdminToolsDropdown
                items={[
                  {
                    label: 'Corrigir Valores Pagos',
                    hint: 'Soma multa/juros/desconto ao Pago',
                    icon: <RotateCcw className="h-4 w-4 text-amber-600" />,
                    testId: 'fix-paid-amount-btn',
                    onClick: async () => {
                      if (!window.confirm('Corrigir valores pagos (Pago) em contas antigas com multa/juros? Isso ajusta o campo paid_amount para somar multa/juros/desconto.')) return;
                      try {
                        const res = await axios.post(`${API_URL}/api/finance/payables/backfill-paid-amount`, {}, { headers });
                        toast.success(res.data.message || 'Backfill concluído');
                        fetchPayables();
                      } catch (e) {
                        toast.error(e.response?.data?.detail || 'Erro no backfill');
                      }
                    },
                  },
                ]}
              />
            )}

            <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
              <DialogTrigger asChild>
                <Button className="h-9 bg-[#001859] hover:bg-[#001859]/90" data-testid="new-payable-btn">
                  <Plus className="h-4 w-4 mr-1.5" />Nova Conta
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto"><DialogHeader><DialogTitle>Nova Conta a Pagar</DialogTitle><DialogDescription>Cadastre um título avulso ou parcelado</DialogDescription></DialogHeader><form onSubmit={handleCreate}>
              <div className="grid gap-4 py-4">
                {/* Tipo de lançamento */}
                <div className="flex flex-wrap items-center gap-4 p-3 bg-muted/50 rounded-lg">
                  <div className="flex items-center gap-2">
                    <Switch 
                      checked={formData.is_installment} 
                      onCheckedChange={(v) => setFormData({...formData, is_installment: v, is_recurring: false})} 
                      data-testid="toggle-installment" 
                    />
                    <Label className="text-sm">Parcelada</Label>
                  </div>
                  <div className="flex items-center gap-2">
                    <Switch 
                      checked={formData.is_recurring} 
                      onCheckedChange={(v) => setFormData({...formData, is_recurring: v, is_installment: false})} 
                      data-testid="toggle-recurring" 
                    />
                    <Label className="text-sm">Despesa Fixa/Recorrente</Label>
                  </div>
                  <div className="flex items-center gap-2">
                    <Switch 
                      checked={formData.is_antecipacao} 
                      onCheckedChange={(v) => setFormData({...formData, is_antecipacao: v})} 
                      data-testid="toggle-antecipacao" 
                    />
                    <Label className="text-sm">Antecipação</Label>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Fornecedor *</Label>
                    <div className="flex gap-2">
                      <Select 
                        value={formData.vendor_id || ''} 
                        onValueChange={(v) => {
                          const vendor = vendors.find(vn => vn.id === v);
                          setFormData({...formData, vendor_id: v, vendor_name: vendor?.name || ''});
                        }}
                      >
                        <SelectTrigger className="flex-1" data-testid="select-vendor">
                          <SelectValue placeholder={formData.vendor_name || "Selecione ou digite..."} />
                        </SelectTrigger>
                        <SelectContent>
                          {vendors.map(v => (<SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>))}
                        </SelectContent>
                      </Select>
                      <Button type="button" variant="outline" size="icon" onClick={() => setIsCreateVendorOpen(true)} title="Criar fornecedor">
                        <Plus className="h-4 w-4" />
                      </Button>
                    </div>
                    <Input 
                      value={formData.vendor_name} 
                      onChange={(e) => setFormData({...formData, vendor_name: e.target.value, vendor_id: ''})} 
                      placeholder="Ou digite o nome..." 
                      className="text-sm h-8"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Categoria *</Label>
                    <div className="flex gap-2">
                      <Select value={formData.category_id} onValueChange={(v) => setFormData({...formData, category_id: v})}>
                        <SelectTrigger className="flex-1" data-testid="select-category"><SelectValue placeholder="Selecione..." /></SelectTrigger>
                        <SelectContent>{categories.map(c => (<SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>))}</SelectContent>
                      </Select>
                      <Button type="button" variant="outline" size="icon" onClick={() => setIsCreateCategoryOpen(true)} title="Criar categoria">
                        <Plus className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label>Descrição *</Label>
                  <Input value={formData.description} onChange={(e) => setFormData({...formData, description: e.target.value})} required data-testid="input-description" />
                </div>

                {/* Dados do Documento */}
                <div className="grid grid-cols-4 gap-4">
                  <div className="space-y-2">
                    <Label>Nº do Documento</Label>
                    <Input value={formData.invoice_number} onChange={(e) => setFormData({...formData, invoice_number: e.target.value})} placeholder="Ex: 12345" data-testid="input-invoice-number" />
                  </div>
                  <div className="space-y-2">
                    <Label>Nº da Nota Fiscal</Label>
                    <Input value={formData.nf_number} onChange={(e) => setFormData({...formData, nf_number: e.target.value})} placeholder="Ex: 001234" data-testid="input-nf-number" />
                  </div>
                  <div className="space-y-2">
                    <Label>Data Emissão Doc.</Label>
                    <Input type="date" value={formData.invoice_date} onChange={(e) => setFormData({...formData, invoice_date: e.target.value})} data-testid="input-invoice-date" />
                  </div>
                  <div className="space-y-2">
                    <Label>Competência</Label>
                    <Input type="date" value={formData.competence_date} onChange={(e) => setFormData({...formData, competence_date: e.target.value})} data-testid="input-competence" />
                  </div>
                </div>

                {formData.is_installment ? (
                  <>
                    <Separator />
                    <div className="text-sm font-medium text-muted-foreground">Dados do Parcelamento</div>
                    <div className="grid grid-cols-3 gap-4">
                      <div className="space-y-2">
                        <Label>Valor Total (R$) *</Label>
                        <Input type="number" step="0.01" min="0" value={formData.total_amount} onChange={(e) => setFormData({...formData, total_amount: e.target.value})} required data-testid="input-total-amount" />
                      </div>
                      <div className="space-y-2">
                        <Label>Nº Parcelas *</Label>
                        <Input type="number" min="2" max="300" value={formData.num_installments} onChange={(e) => setFormData({...formData, num_installments: e.target.value})} required data-testid="input-num-installments" />
                      </div>
                      <div className="space-y-2">
                        <Label>1º Vencimento *</Label>
                        <Input type="date" value={formData.first_due_date} onChange={(e) => setFormData({...formData, first_due_date: e.target.value})} required data-testid="input-first-due" />
                      </div>
                    </div>
                    {formData.total_amount && formData.num_installments && (
                      <div className="text-sm text-muted-foreground">
                        Parcela mensal: <strong>{formatCurrency(parseFloat(formData.total_amount) / parseInt(formData.num_installments))}</strong>
                      </div>
                    )}
                  </>
                ) : formData.is_recurring ? (
                  <>
                    <Separator />
                    <div className="text-sm font-medium text-muted-foreground">Despesa Fixa/Recorrente</div>
                    <p className="text-xs text-muted-foreground">Ex: Energia, Aluguel, Internet - será lançada automaticamente em cada período</p>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Valor Fixo (R$) *</Label>
                        <Input type="number" step="0.01" min="0" value={formData.amount} onChange={(e) => setFormData({...formData, amount: e.target.value})} required data-testid="input-recurring-amount" />
                      </div>
                      <div className="space-y-2">
                        <Label>1º Vencimento *</Label>
                        <Input type="date" value={formData.due_date} onChange={(e) => setFormData({...formData, due_date: e.target.value})} required data-testid="input-recurring-first-due" />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Periodicidade *</Label>
                        <Select value={formData.recurrence_type} onValueChange={(v) => setFormData({...formData, recurrence_type: v})}>
                          <SelectTrigger><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="weekly">Semanal</SelectItem>
                            <SelectItem value="biweekly">Quinzenal</SelectItem>
                            <SelectItem value="monthly">Mensal</SelectItem>
                            <SelectItem value="quarterly">Trimestral</SelectItem>
                            <SelectItem value="semiannual">Semestral</SelectItem>
                            <SelectItem value="annual">Anual</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label>Duração *</Label>
                        <Select value={formData.recurrence_duration} onValueChange={(v) => setFormData({...formData, recurrence_duration: v})}>
                          <SelectTrigger><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="indefinite">Indeterminada</SelectItem>
                            <SelectItem value="fixed">Prazo Definido</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    {formData.recurrence_duration === 'fixed' && (
                      <div className="space-y-2">
                        <Label>Quantidade de repetições *</Label>
                        <Input type="number" min="2" max="300" value={formData.recurrence_count} onChange={(e) => setFormData({...formData, recurrence_count: e.target.value})} required placeholder="Ex: 12" />
                      </div>
                    )}
                    <div className="p-3 bg-blue-50 text-blue-800 rounded-lg text-sm">
                      <strong>Resumo:</strong> {formData.amount && `R$ ${parseFloat(formData.amount).toFixed(2)}`} 
                      {formData.recurrence_type === 'weekly' && ' toda semana'}
                      {formData.recurrence_type === 'biweekly' && ' a cada 15 dias'}
                      {formData.recurrence_type === 'monthly' && ' todo mês'}
                      {formData.recurrence_type === 'quarterly' && ' a cada 3 meses'}
                      {formData.recurrence_type === 'semiannual' && ' a cada 6 meses'}
                      {formData.recurrence_type === 'annual' && ' todo ano'}
                      {formData.recurrence_duration === 'indefinite' ? ' (sem fim)' : ` por ${formData.recurrence_count || 0} vezes`}
                    </div>
                  </>
                ) : (
                  <div className="grid grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <Label>Vencimento *</Label>
                      <Input type="date" value={formData.due_date} onChange={(e) => setFormData({...formData, due_date: e.target.value})} required data-testid="input-due-date" />
                    </div>
                    <div className="space-y-2">
                      <Label>Previsão Pgto</Label>
                      <Input type="date" value={formData.forecast_date} onChange={(e) => setFormData({...formData, forecast_date: e.target.value})} data-testid="input-forecast-date" title="Data prevista para pagamento (usada no fluxo de caixa)" />
                    </div>
                    <div className="space-y-2">
                      <Label>Valor Bruto (R$) *</Label>
                      <Input type="number" step="0.01" min="0" value={formData.amount} onChange={(e) => setFormData({...formData, amount: e.target.value})} required data-testid="input-amount" />
                    </div>
                  </div>
                )}

                {/* Tax Retention Section */}
                <div className="border rounded-lg p-4 space-y-4 bg-slate-50">
                  <div className="flex items-center gap-3">
                    <Switch
                      id="has_retention"
                      checked={formData.has_retention}
                      onCheckedChange={(checked) => setFormData({...formData, has_retention: checked, retention_amount: '', retention_rate: ''})}
                    />
                    <div>
                      <label htmlFor="has_retention" className="font-medium cursor-pointer">
                        Tem Retenção de Imposto
                      </label>
                      <p className="text-xs text-muted-foreground">
                        IR, INSS, PIS/COFINS, CSLL, ISS
                      </p>
                    </div>
                  </div>
                  
                  {formData.has_retention && (
                    <div className="grid grid-cols-3 gap-4">
                      <div className="space-y-2">
                        <Label>Tipo de Retenção</Label>
                        <Select value={formData.retention_type} onValueChange={(v) => setFormData({...formData, retention_type: v})}>
                          <SelectTrigger><SelectValue placeholder="Selecione..." /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="ir">IR (Imposto de Renda)</SelectItem>
                            <SelectItem value="inss">INSS</SelectItem>
                            <SelectItem value="pis_cofins">PIS/COFINS</SelectItem>
                            <SelectItem value="csll">CSLL</SelectItem>
                            <SelectItem value="iss">ISS</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label>Alíquota (%)</Label>
                        <Input 
                          type="number" 
                          step="0.01" 
                          min="0" 
                          max="100"
                          value={formData.retention_rate} 
                          onChange={(e) => {
                            const rate = parseFloat(e.target.value) || 0;
                            const amount = parseFloat(formData.amount) || 0;
                            const retained = (amount * rate / 100).toFixed(2);
                            setFormData({...formData, retention_rate: e.target.value, retention_amount: retained});
                          }}
                          placeholder="Ex: 1.5"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Valor Retido (R$)</Label>
                        <Input 
                          type="number" 
                          step="0.01" 
                          min="0" 
                          value={formData.retention_amount} 
                          onChange={(e) => setFormData({...formData, retention_amount: e.target.value})}
                        />
                      </div>
                    </div>
                  )}
                  
                  {formData.has_retention && formData.amount && (
                    <div className="p-3 bg-blue-50 text-blue-800 rounded-lg text-sm">
                      <div className="flex justify-between">
                        <span>Valor Bruto:</span>
                        <span className="font-mono">{formatCurrency(parseFloat(formData.amount) || 0)}</span>
                      </div>
                      <div className="flex justify-between text-red-600">
                        <span>(-) Retenção {formData.retention_type?.toUpperCase()}:</span>
                        <span className="font-mono">{formatCurrency(parseFloat(formData.retention_amount) || 0)}</span>
                      </div>
                      <Separator className="my-2" />
                      <div className="flex justify-between font-bold">
                        <span>Valor Líquido a Pagar:</span>
                        <span className="font-mono">{formatCurrency((parseFloat(formData.amount) || 0) - (parseFloat(formData.retention_amount) || 0))}</span>
                      </div>
                    </div>
                  )}
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Centro de Custo *</Label>
                    <div className="flex gap-2">
                      <Select value={formData.cost_center_id} onValueChange={(v) => setFormData({...formData, cost_center_id: v})}>
                        <SelectTrigger className="flex-1"><SelectValue placeholder="Selecione..." /></SelectTrigger>
                        <SelectContent>{costCenters.map(c => (<SelectItem key={c.id} value={c.id}>{c.code ? `${c.code} - ` : ''}{c.name}</SelectItem>))}</SelectContent>
                      </Select>
                      <Button type="button" variant="outline" size="icon" onClick={() => setIsCreateCostCenterOpen(true)} title="Criar centro de custo">
                        <Plus className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>Forma de Pagamento</Label>
                    <Select value={formData.payment_method} onValueChange={(v) => setFormData({...formData, payment_method: v})}>
                      <SelectTrigger><SelectValue placeholder="Selecione..." /></SelectTrigger>
                      <SelectContent>{PAYMENT_METHODS.map(m => (<SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>))}</SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Conta Bancária (Destino)</Label>
                    <Select value={formData.bank_account_id} onValueChange={(v) => setFormData({...formData, bank_account_id: v})}>
                      <SelectTrigger><SelectValue placeholder="Selecione a conta..." /></SelectTrigger>
                      <SelectContent>
                        {bankAccounts.map(b => (
                          <SelectItem key={b.id} value={b.id}>
                            {b.bank_name} - {b.account_number}
                            {b.is_third_party && ' (Terceiro)'}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-muted-foreground">Conta onde será pago. Usado para filtros e fluxo de caixa.</p>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Observações</Label>
                  <Textarea value={formData.notes} onChange={(e) => setFormData({...formData, notes: e.target.value})} rows={2} />
                </div>
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setIsCreateOpen(false)}>Cancelar</Button>
                <Button type="submit" disabled={isSubmitting} data-testid="submit-payable">
                  {isSubmitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                  {formData.is_installment ? 'Criar Parcelas' : 'Salvar'}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Create Category Modal */}
        <Dialog open={isCreateCategoryOpen} onOpenChange={setIsCreateCategoryOpen}>
          <DialogContent className="max-w-sm">
            <DialogHeader>
              <DialogTitle>Nova Categoria</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Nome da Categoria *</Label>
                <Input 
                  value={newCategoryName} 
                  onChange={(e) => setNewCategoryName(e.target.value)} 
                  placeholder="Ex: Material de Escritório"
                />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setIsCreateCategoryOpen(false)}>Cancelar</Button>
              <Button onClick={handleCreateCategory} disabled={!newCategoryName.trim()}>Criar</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Create Vendor Modal */}
        <Dialog open={isCreateVendorOpen} onOpenChange={setIsCreateVendorOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Novo Fornecedor</DialogTitle>
              <DialogDescription>Cadastre rapidamente um novo fornecedor</DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Nome/Razão Social *</Label>
                <Input 
                  value={newVendorData.name} 
                  onChange={(e) => setNewVendorData({...newVendorData, name: e.target.value})} 
                  placeholder="Nome do fornecedor"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>CNPJ/CPF</Label>
                  <Input 
                    value={newVendorData.document} 
                    onChange={(e) => setNewVendorData({...newVendorData, document: e.target.value})} 
                    placeholder="00.000.000/0000-00"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Telefone</Label>
                  <Input 
                    value={newVendorData.phone} 
                    onChange={(e) => setNewVendorData({...newVendorData, phone: e.target.value})} 
                    placeholder="(00) 00000-0000"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>E-mail</Label>
                <Input 
                  type="email"
                  value={newVendorData.email} 
                  onChange={(e) => setNewVendorData({...newVendorData, email: e.target.value})} 
                  placeholder="email@fornecedor.com"
                />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setIsCreateVendorOpen(false)}>Cancelar</Button>
              <Button onClick={handleCreateVendor} disabled={!newVendorData.name.trim()}>Criar Fornecedor</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Create Cost Center Modal */}
        <Dialog open={isCreateCostCenterOpen} onOpenChange={setIsCreateCostCenterOpen}>
          <DialogContent className="max-w-sm">
            <DialogHeader>
              <DialogTitle>Novo Centro de Custo</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Código *</Label>
                <Input 
                  value={newCostCenterData.code} 
                  onChange={(e) => setNewCostCenterData({...newCostCenterData, code: e.target.value})} 
                  placeholder="Ex: ADM, PROD, VND"
                />
              </div>
              <div className="space-y-2">
                <Label>Nome *</Label>
                <Input 
                  value={newCostCenterData.name} 
                  onChange={(e) => setNewCostCenterData({...newCostCenterData, name: e.target.value})} 
                  placeholder="Ex: Administrativo"
                />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setIsCreateCostCenterOpen(false)}>Cancelar</Button>
              <Button onClick={handleCreateCostCenter} disabled={!newCostCenterData.code.trim() || !newCostCenterData.name.trim()}>Criar</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
          </div>{/* /ml-auto ações */}
        </div>{/* /linha 1 filtros */}

        {/* Linha 2: Filtros rápidos + Agrupamento */}
        <div className="flex flex-wrap gap-2 items-center">
          <QuickFilters
            variant="payables"
            vendors={vendors}
            categories={categories}
            costCenters={costCenters}
            vendorFilter={vendorFilter}
            setVendorFilter={setVendorFilter}
            categoryFilter={categoryFilter}
            setCategoryFilter={setCategoryFilter}
            costCenterFilter={costCenterFilter}
            setCostCenterFilter={setCostCenterFilter}
          />
          <div className="ml-auto">
            <GroupByDropdown value={groupBy} onChange={setGroupBy} variant="payables" />
          </div>
        </div>
      </div>{/* /toolbar wrapper */}

      {/* ===== Chips de filtros ativos ===== */}
      {hasActiveFilters || (filterInstallment && filterInstallment !== 'all') || groupInstallments ? (
        <div className="flex flex-wrap items-center gap-2">
          <ActiveFilterChips
            chips={[
              ...buildFilterChips({
                searchTerm,
                clearSearch: () => setSearchTerm(''),
                filterStatus,
                clearStatus: () => setFilterStatus('all'),
                statusLabels: { unpaid: 'Em aberto', pending: 'Pendente', paid: 'Pago', partially_paid: 'Parc. Pago', cancelled: 'Cancelado' },
                filterInstallment,
                clearInstallment: () => setFilterInstallment('all'),
                groupInstallments,
                clearGroupInstallments: () => setGroupInstallments(false),
                advancedFilters,
                setAdvancedFilters,
                filterableColumnsByKey: Object.fromEntries(filterableColumns.map(c => [c.key, c])),
              }),
              ...(vendorFilter !== 'all' ? [{
                key: 'vendor',
                label: 'Fornecedor',
                value: (vendors.find(v => (v.id || v.name) === vendorFilter)?.name || vendorFilter).slice(0, 24),
                onRemove: () => setVendorFilter('all'),
              }] : []),
              ...(categoryFilter !== 'all' ? [{
                key: 'category',
                label: 'Categoria',
                value: (categories.find(c => (c.id || c.name) === categoryFilter)?.name || categoryFilter).slice(0, 24),
                onRemove: () => setCategoryFilter('all'),
              }] : []),
              ...(costCenterFilter !== 'all' ? [{
                key: 'cost_center',
                label: 'CNPJ',
                value: (costCenters.find(c => c.id === costCenterFilter)?.name || '').slice(0, 24),
                onRemove: () => setCostCenterFilter('all'),
              }] : []),
              ...(quickPreset ? [{
                key: 'preset',
                label: 'Atalho',
                value: {
                  overdue_today: 'Vencidos + Hoje', overdue: 'Vencidos', today: 'Vence hoje', this_week: 'Esta semana',
                  all_open: 'Em aberto', paid_this_month: 'Pagos no mês',
                }[quickPreset] || quickPreset,
                onRemove: () => { setQuickPreset(null); setFilterStatus('all'); },
              }] : []),
            ]}
          />
          <Button
            variant="ghost"
            size="sm"
            className="h-6 px-2 text-[11px] text-rose-600 hover:text-rose-700 hover:bg-rose-50"
            onClick={() => {
              setSearchTerm('');
              setFilterStatus('all');
              setFilterInstallment('all');
              setGroupInstallments(false);
              setAdvancedFilters({});
              setVendorFilter('all');
              setCategoryFilter('all');
              setCostCenterFilter('all');
              setQuickPreset(null);
            }}
            data-testid="clear-all-filters-btn"
          >
            <X className="h-3 w-3 mr-1" />Limpar tudo
          </Button>
        </div>
      ) : null}

      {/* ===== Barra de seleção múltipla ===== */}
      {selectedItems.length > 0 && (
        <div className="flex items-center gap-4 px-4 py-2 bg-primary/10 rounded-lg border border-primary/20">
          <Checkbox
            checked={selectedItems.length === filteredPayables.flatMap(([_, items]) => items).length}
            onCheckedChange={toggleSelectAll}
          />
          <span className="text-sm font-medium">{selectedItems.length} selecionado(s)</span>
          <span className="text-sm text-muted-foreground">Total: <span className="font-mono font-medium text-foreground">{formatCurrency(getSelectedTotal())}</span></span>
          <div className="flex-1" />
          <Button variant="outline" size="sm" onClick={() => setIsBulkEditOpen(true)}>
            <Edit2 className="h-4 w-4 mr-2" />Editar Selecionados
          </Button>
          <Button variant="default" size="sm" onClick={() => setIsBulkPayOpen(true)} className="bg-green-600 hover:bg-green-700" data-testid="bulk-pay-btn">
            <CheckCircle className="h-4 w-4 mr-2" />Pagar Selecionados
          </Button>
          {isAdmin && (
            <Button variant="destructive" size="sm" onClick={() => setIsBulkDeleteOpen(true)}>
              <Trash2 className="h-4 w-4 mr-2" />Excluir Selecionados
            </Button>
          )}
          <Button variant="outline" size="sm" onClick={() => setSelectedItems([])}>Limpar Seleção</Button>
        </div>
      )}

      {/* ===== Visão Agrupada (quando groupBy != 'none') ===== */}
      {groupBy !== 'none' && (() => {
        const allFiltered = filteredPayables.flatMap(([, items]) => items);
        const grouped = groupLedgerItems(allFiltered, groupBy, 'amount');
        const grouping_label = ({ vendor: 'Fornecedor', category: 'Categoria', cost_center: 'CNPJ/Centro', month: 'Mês' })[groupBy];
        if (!grouped || grouped.length === 0) {
          return (
            <Card className="border-dashed border-blue-300 bg-blue-50/30" data-testid="grouped-view">
              <CardContent className="p-4 text-center text-sm text-muted-foreground">
                <Layers className="h-5 w-5 mx-auto mb-1 text-blue-400" />
                Agrupamento por <strong className="text-[#001859]">{grouping_label}</strong> ativo, mas nenhum lançamento no filtro atual.
              </CardContent>
            </Card>
          );
        }
        const grandTotal = grouped.reduce((s, g) => s + g.total, 0);
        const grandCount = grouped.reduce((s, g) => s + g.count, 0);
        return (
          <Card className="border-blue-200" data-testid="grouped-view">
            <CardContent className="p-0">
              <div className="p-3 border-b bg-blue-50/40 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-semibold text-[#001859]">
                    Agrupado por {grouping_label}
                  </span>
                  <Badge variant="secondary" className="text-xs">{grouped.length} grupo(s)</Badge>
                </div>
                <div className="text-sm font-mono font-bold text-[#001859]">{formatCurrency(grandTotal)} • {grandCount} título(s)</div>
              </div>
              <div className="max-h-80 overflow-y-auto">
                <table className="w-full text-xs md:text-sm">
                  <thead className="bg-slate-50 sticky top-0">
                    <tr>
                      <th className="px-3 py-2 text-left font-semibold">Grupo</th>
                      <th className="px-3 py-2 text-center font-semibold">Qtd</th>
                      <th className="px-3 py-2 text-right font-semibold">Total</th>
                      <th className="px-3 py-2 text-right font-semibold">% do total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {grouped.map(g => (
                      <tr key={g.key} className="border-b hover:bg-blue-50/30">
                        <td className="px-3 py-1.5 max-w-[360px] truncate font-medium" title={g.key}>{g.key}</td>
                        <td className="px-3 py-1.5 text-center text-xs text-slate-500">{g.count}</td>
                        <td className="px-3 py-1.5 text-right font-mono font-bold tabular-nums">{formatCurrency(g.total)}</td>
                        <td className="px-3 py-1.5 text-right text-xs text-blue-600 font-semibold">{grandTotal ? ((g.total / grandTotal) * 100).toFixed(1) : '0.0'}%</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        );
      })()}

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          {payablesLoading ? (
            <div className="p-6 space-y-4">{[...Array(5)].map((_, i) => (<Skeleton key={i} className="h-12 w-full" />))}</div>
          ) : (
            <div className="overflow-x-auto">
              <Table className="min-w-[900px]">
                <TableHeader>
                  <TableRow>
                    {isColumnVisible('obs') && <TableHead className="w-12 text-center">Obs</TableHead>}
                    {isColumnVisible('checkbox') && (
                      <TableHead className="w-10 sticky left-0 bg-card z-10">
                        <Checkbox 
                          checked={selectedItems.length > 0 && selectedItems.length === filteredPayables.flatMap(([_, items]) => items).length}
                          onCheckedChange={toggleSelectAll}
                        />
                      </TableHead>
                    )}
                    {isColumnVisible('due_date') && (
                      <TableHead className="cursor-pointer hover:bg-muted/50" onClick={() => handleSort('due_date')}>
                        <div className="flex items-center gap-1">
                          Vencimento
                          {sortConfig.key === 'due_date' ? (sortConfig.direction === 'asc' ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />) : <ArrowUpDown className="h-3 w-3 opacity-30" />}
                        </div>
                      </TableHead>
                    )}
                    {isColumnVisible('forecast_date') && (
                      <TableHead className="cursor-pointer hover:bg-muted/50" onClick={() => handleSort('forecast_date')}>
                        <div className="flex items-center gap-1">
                          Previsão
                          {sortConfig.key === 'forecast_date' ? (sortConfig.direction === 'asc' ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />) : <ArrowUpDown className="h-3 w-3 opacity-30" />}
                        </div>
                      </TableHead>
                    )}
                    {isColumnVisible('payment_date') && (
                      <TableHead className="cursor-pointer hover:bg-muted/50" onClick={() => handleSort('payment_date')}>
                        <div className="flex items-center gap-1">
                          Pagamento
                          {sortConfig.key === 'payment_date' ? (sortConfig.direction === 'asc' ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />) : <ArrowUpDown className="h-3 w-3 opacity-30" />}
                        </div>
                      </TableHead>
                    )}
                    {isColumnVisible('vendor_name') && (
                      <TableHead className="cursor-pointer hover:bg-muted/50" onClick={() => handleSort('vendor_name')}>
                        <div className="flex items-center gap-1">
                          Fornecedor
                          {sortConfig.key === 'vendor_name' ? (sortConfig.direction === 'asc' ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />) : <ArrowUpDown className="h-3 w-3 opacity-30" />}
                        </div>
                      </TableHead>
                    )}
                    {isColumnVisible('category') && (
                      <TableHead className="cursor-pointer hover:bg-muted/50" onClick={() => handleSort('category')}>
                        <div className="flex items-center gap-1">
                          Categoria
                          {sortConfig.key === 'category' ? (sortConfig.direction === 'asc' ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />) : <ArrowUpDown className="h-3 w-3 opacity-30" />}
                        </div>
                      </TableHead>
                    )}
                    {isColumnVisible('description') && (
                      <TableHead className="cursor-pointer hover:bg-muted/50" onClick={() => handleSort('description')}>
                        <div className="flex items-center gap-1">
                          Descrição
                          {sortConfig.key === 'description' ? (sortConfig.direction === 'asc' ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />) : <ArrowUpDown className="h-3 w-3 opacity-30" />}
                        </div>
                      </TableHead>
                    )}
                    {isColumnVisible('invoice_number') && (
                      <TableHead className="cursor-pointer hover:bg-muted/50" onClick={() => handleSort('invoice_number')}>
                        <div className="flex items-center gap-1">
                          Nº Doc
                          {sortConfig.key === 'invoice_number' ? (sortConfig.direction === 'asc' ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />) : <ArrowUpDown className="h-3 w-3 opacity-30" />}
                        </div>
                      </TableHead>
                    )}
                    {isColumnVisible('nf_number') && (
                      <TableHead className="cursor-pointer hover:bg-muted/50" onClick={() => handleSort('nf_number')}>
                        <div className="flex items-center gap-1">
                          Nº NF
                          {sortConfig.key === 'nf_number' ? (sortConfig.direction === 'asc' ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />) : <ArrowUpDown className="h-3 w-3 opacity-30" />}
                        </div>
                      </TableHead>
                    )}
                    {isColumnVisible('bank_account') && <TableHead>Conta</TableHead>}
                    {isColumnVisible('amount') && (
                      <TableHead className="text-right cursor-pointer hover:bg-muted/50" onClick={() => handleSort('amount')}>
                        <div className="flex items-center justify-end gap-1">
                          Valor
                          {sortConfig.key === 'amount' ? (sortConfig.direction === 'asc' ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />) : <ArrowUpDown className="h-3 w-3 opacity-30" />}
                        </div>
                      </TableHead>
                    )}
                    {isColumnVisible('paid_amount') && <TableHead className="text-right">Pago</TableHead>}
                    {isColumnVisible('status') && (
                      <TableHead className="cursor-pointer hover:bg-muted/50" onClick={() => handleSort('status')}>
                        <div className="flex items-center gap-1">
                          Status
                          {sortConfig.key === 'status' ? (sortConfig.direction === 'asc' ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />) : <ArrowUpDown className="h-3 w-3 opacity-30" />}
                        </div>
                      </TableHead>
                    )}
                    {isColumnVisible('actions') && <TableHead className="text-right">Ações</TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredPayables.length === 0 ? (
                  <TableRow><TableCell colSpan={visibleColumns.length} className="text-center py-8 text-muted-foreground">Nenhuma conta encontrada</TableCell></TableRow>
                ) : (
                  filteredPayables.map(([groupKey, items]) => {
                    const isGroup = items.length > 1;
                    const expanded = expandedGroups[groupKey];
                    const firstItem = items[0];
                    const groupTotal = items.reduce((sum, p) => sum + p.amount, 0);
                    const paidCount = items.filter(p => p.status === 'paid').length;

                    if (isGroup) {
                      return (
                        <React.Fragment key={groupKey}>
                          {/* Group Header — usa colSpan dinâmico para se ajustar às colunas visíveis */}
                          <TableRow className="bg-muted/30 cursor-pointer hover:bg-muted/50" onClick={() => toggleGroup(groupKey)}>
                            <TableCell colSpan={Math.max(1, visibleColumns.length - 2)}>
                              <div className="flex items-center gap-2">
                                {expanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                                <Badge variant="outline" className="text-xs">Parcelado</Badge>
                                <span className="font-medium">{firstItem.vendor_name}</span>
                                <span className="text-muted-foreground text-xs">• {firstItem.description?.split(' - Parcela')[0]}</span>
                              </div>
                            </TableCell>
                            <TableCell className="text-right font-mono">{formatCurrency(groupTotal)}</TableCell>
                            <TableCell>
                              <Badge variant="secondary">{paidCount}/{items.length} pagas</Badge>
                            </TableCell>
                          </TableRow>
                          {/* Group Items */}
                          {expanded && items.sort((a, b) => a.installment_number - b.installment_number).map((payable) => {
                            const overdue = payable.status === 'pending' && isOverdue(payable.due_date);
                            const isPaid = payable.status === 'paid' || payable.status === 'partially_paid';
                            const isSelected = selectedItems.includes(payable.id);
                            const rowClass = isSelected
                              ? 'bg-primary/10'
                              : isPaid 
                                ? payable.status === 'partially_paid' ? 'bg-amber-50 hover:bg-amber-100' : 'bg-green-50 hover:bg-green-100' 
                                : overdue 
                                  ? 'bg-red-50 hover:bg-red-100' 
                                  : 'bg-background hover:bg-muted/50';
                            return (
                              <TableRow key={payable.id} className={rowClass} data-testid={`payable-row-${payable.id}`}>
                                <TableCell className="text-center" onClick={(e) => e.stopPropagation()}>
                                  <NotesCell
                                    value={localComments[payable.id] !== undefined ? localComments[payable.id] : (payable.notes || '')}
                                    onSave={(v) => { setLocalComments(prev => ({ ...prev, [payable.id]: v })); saveComment(payable.id, v); }}
                                    testId={`notes-btn-${payable.id}`}
                                  />
                                </TableCell>
                                <TableCell onClick={(e) => e.stopPropagation()}>
                                  <Checkbox 
                                    checked={isSelected}
                                    onCheckedChange={() => toggleSelectItem(payable.id)}
                                  />
                                </TableCell>
                                {isColumnVisible('due_date') && (
                                  <TableCell className={overdue ? 'text-red-600 font-medium' : isPaid ? 'text-green-700' : ''}>
                                    {formatDate(payable.due_date)}
                                  </TableCell>
                                )}
                                {isColumnVisible('forecast_date') && (
                                  <TableCell className="hidden md:table-cell text-muted-foreground">
                                    {payable.forecast_date ? formatDate(payable.forecast_date) : payable.competence_date ? formatDate(payable.competence_date) : '-'}
                                  </TableCell>
                                )}
                                {isColumnVisible('payment_date') && (
                                  <TableCell className="hidden md:table-cell">
                                    {payable.payment_date ? formatDate(payable.payment_date) : '-'}
                                  </TableCell>
                                )}
                                {isColumnVisible('vendor_name') && (
                                  <TableCell className="text-muted-foreground text-sm">
                                    Parcela {payable.installment_number}/{payable.installment_total}
                                  </TableCell>
                                )}
                                {isColumnVisible('category') && (
                                  <TableCell className="hidden md:table-cell">{payable.category || '-'}</TableCell>
                                )}
                                {isColumnVisible('description') && (
                                  <TableCell className="max-w-[200px] truncate hidden lg:table-cell">
                                    {payable.description}
                                    {payable.is_antecipacao && <span className="ml-1 text-[10px] bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded-full font-medium">Ant.</span>}
                                  </TableCell>
                                )}
                                {isColumnVisible('invoice_number') && (
                                  <TableCell className="hidden lg:table-cell font-mono text-sm">
                                    {payable.invoice_number || '-'}
                                  </TableCell>
                                )}
                                {isColumnVisible('nf_number') && (
                                  <TableCell className="hidden lg:table-cell font-mono text-sm">
                                    {payable.nf_number || '-'}
                                  </TableCell>
                                )}
                                {isColumnVisible('bank_account') && (
                                  <TableCell className="hidden lg:table-cell text-muted-foreground">
                                    {payable.bank_account_name || '-'}
                                  </TableCell>
                                )}
                                {isColumnVisible('amount') && (
                                  <TableCell className={`text-right font-mono font-medium ${isPaid ? 'text-green-700' : overdue ? 'text-red-600' : ''}`}>
                                    {payable.status === 'partially_paid'
                                      ? (
                                        <div className="leading-tight">
                                          <div className="text-green-700 font-semibold">
                                            {formatCurrency(payable.paid_amount || 0)}
                                          </div>
                                          <div className="text-[10px] text-muted-foreground">
                                            de {formatCurrency(payable.amount || 0)}
                                          </div>
                                          <div className="text-[10px] text-amber-700 font-medium">
                                            saldo {formatCurrency(Math.max(0, (parseFloat(payable.amount) || 0) - (parseFloat(payable.paid_amount) || 0)))}
                                          </div>
                                        </div>
                                      )
                                      : (
                                        <span title={payable.retention_amount ? `Retenção: ${formatCurrency(payable.retention_amount)} (${(payable.retention_type || 'IR').toUpperCase()})` : ''}>
                                          {formatCurrency(payable.net_amount || payable.amount)}
                                        </span>
                                      )
                                    }
                                  </TableCell>
                                )}
                                {isColumnVisible('paid_amount') && (
                                  <TableCell className={`text-right font-mono font-medium ${isPaid ? 'text-green-700' : ''}`}>
                                    {isPaid ? formatCurrency(payable.paid_amount || payable.amount) : '-'}
                                    {isPaid && payable.penalty_amount > 0 && (
                                      <span className="text-[10px] text-red-600 block">+{formatCurrency(payable.penalty_amount)} multa</span>
                                    )}
                                    {isPaid && payable.interest_amount > 0 && (
                                      <span className="text-[10px] text-red-600 block">+{formatCurrency(payable.interest_amount)} juros</span>
                                    )}
                                  </TableCell>
                                )}
                                {isColumnVisible('status') && (
                                  <TableCell>
                                    <Badge className={getStatusBadgeClass(overdue ? 'overdue' : payable.status)}>
                                      {getStatusLabel(overdue ? 'overdue' : payable.status)}
                                    </Badge>
                                  </TableCell>
                                )}
                                {isColumnVisible('actions') && (
                                <TableCell className="text-right">
                                  <div className="flex gap-1 justify-end">
                                    <Button size="sm" variant="ghost" onClick={() => openViewDialog(payable)} title="Ver detalhes">
                                      <Eye className="h-4 w-4" />
                                    </Button>
                                    <Button size="sm" variant="ghost" onClick={() => openEditDialog(payable)} data-testid={`edit-btn-${payable.id}`} title="Editar">
                                      <Edit2 className="h-4 w-4" />
                                    </Button>
                                    <Button size="sm" variant="ghost" onClick={() => openReplicateDialog(payable)} title="Replicar">
                                      <Copy className="h-4 w-4" />
                                    </Button>
                                    {(payable.status === 'pending' || payable.status === 'partially_paid') && (
                                      <Button size="sm" variant="outline" onClick={() => openPayDialog(payable)} data-testid={`pay-btn-${payable.id}`} title="Pagar">
                                        <Check className="h-4 w-4" />
                                      </Button>
                                    )}
                                    {isAdmin && <Button size="sm" variant="ghost" className="text-red-600 hover:text-red-700" onClick={() => openDeleteDialog(payable)} title="Excluir">
                                      <Trash2 className="h-4 w-4" />
                                    </Button>}
                                  </div>
                                </TableCell>
                                )}
                              </TableRow>
                            );
                          })}
                        </React.Fragment>
                      );
                    } else {
                      // Single payable
                      const payable = firstItem;
                      const overdue = payable.status === 'pending' && isOverdue(payable.due_date);
                      const isPaid = payable.status === 'paid' || payable.status === 'partially_paid';
                      const isSelected = selectedItems.includes(payable.id);
                      const rowClass = isSelected
                        ? 'bg-primary/10'
                        : isPaid 
                          ? payable.status === 'partially_paid' ? 'bg-amber-50 hover:bg-amber-100' : 'bg-green-50 hover:bg-green-100' 
                          : overdue 
                            ? 'bg-red-50 hover:bg-red-100' 
                            : 'hover:bg-muted/50';
                      return (
                        <TableRow key={payable.id} className={rowClass} data-testid={`payable-row-${payable.id}`}>
                          <TableCell className="text-center" onClick={(e) => e.stopPropagation()}>
                            <NotesCell
                              value={localComments[payable.id] !== undefined ? localComments[payable.id] : (payable.notes || '')}
                              onSave={(v) => { setLocalComments(prev => ({ ...prev, [payable.id]: v })); saveComment(payable.id, v); }}
                              testId={`notes-btn-${payable.id}`}
                            />
                          </TableCell>
                          <TableCell onClick={(e) => e.stopPropagation()}>
                            <Checkbox 
                              checked={isSelected}
                              onCheckedChange={() => toggleSelectItem(payable.id)}
                            />
                          </TableCell>
                          {isColumnVisible('due_date') && (
                            <TableCell className={overdue ? 'text-red-600 font-medium' : isPaid ? 'text-green-700' : ''}>{formatDate(payable.due_date)}</TableCell>
                          )}
                          {isColumnVisible('forecast_date') && (
                            <TableCell className="hidden md:table-cell text-muted-foreground">
                              {payable.forecast_date ? formatDate(payable.forecast_date) : payable.competence_date ? formatDate(payable.competence_date) : '-'}
                            </TableCell>
                          )}
                          {isColumnVisible('payment_date') && (
                            <TableCell className="hidden md:table-cell">
                              {payable.payment_date ? formatDate(payable.payment_date) : '-'}
                            </TableCell>
                          )}
                          {isColumnVisible('vendor_name') && (
                            <TableCell>{payable.vendor_name}</TableCell>
                          )}
                          {isColumnVisible('category') && (
                            <TableCell className="hidden md:table-cell">{payable.category || '-'}</TableCell>
                          )}
                          {isColumnVisible('description') && (
                            <TableCell className="max-w-[200px] truncate hidden lg:table-cell">
                              {payable.description}
                              {payable.is_antecipacao && <span className="ml-1 text-[10px] bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded-full font-medium">Ant.</span>}
                            </TableCell>
                          )}
                          {isColumnVisible('invoice_number') && (
                            <TableCell className="hidden lg:table-cell font-mono text-sm">
                              {payable.invoice_number || '-'}
                            </TableCell>
                          )}
                          {isColumnVisible('nf_number') && (
                            <TableCell className="hidden lg:table-cell font-mono text-sm">
                              {payable.nf_number || '-'}
                            </TableCell>
                          )}
                          {isColumnVisible('bank_account') && (
                            <TableCell className="hidden lg:table-cell text-muted-foreground">
                              {payable.bank_account_name || '-'}
                            </TableCell>
                          )}
                          {isColumnVisible('amount') && (
                            <TableCell className={`text-right font-mono font-medium ${isPaid ? 'text-green-700' : overdue ? 'text-red-600' : ''}`}>
                              {payable.status === 'partially_paid' ? (
                                <div className="leading-tight">
                                  <div className="text-green-700 font-semibold">
                                    {formatCurrency(payable.paid_amount || 0)}
                                  </div>
                                  <div className="text-[10px] text-muted-foreground">
                                    de {formatCurrency(payable.amount || 0)}
                                  </div>
                                  <div className="text-[10px] text-amber-700 font-medium">
                                    saldo {formatCurrency(Math.max(0, (parseFloat(payable.amount) || 0) - (parseFloat(payable.paid_amount) || 0)))}
                                  </div>
                                </div>
                              ) : (
                                <span title={payable.retention_amount ? `Retenção: ${formatCurrency(payable.retention_amount)} (${(payable.retention_type || 'IR').toUpperCase()}${payable.retention_rate ? ` ${payable.retention_rate}%` : ''})` : ''}>
                                  {formatCurrency(payable.net_amount || payable.amount)}
                                </span>
                              )}
                            </TableCell>
                          )}
                          {isColumnVisible('paid_amount') && (
                            <TableCell className={`text-right font-mono font-medium ${isPaid ? 'text-green-700' : ''}`}>
                              {isPaid ? formatCurrency(payable.paid_amount || payable.amount) : '-'}
                              {isPaid && payable.penalty_amount > 0 && (
                                <span className="text-[10px] text-red-600 block">+{formatCurrency(payable.penalty_amount)} multa</span>
                              )}
                              {isPaid && payable.interest_amount > 0 && (
                                <span className="text-[10px] text-red-600 block">+{formatCurrency(payable.interest_amount)} juros</span>
                              )}
                            </TableCell>
                          )}
                          {isColumnVisible('status') && (
                            <TableCell>
                              <Badge className={getStatusBadgeClass(overdue ? 'overdue' : payable.status)}>
                                {getStatusLabel(overdue ? 'overdue' : payable.status)}
                              </Badge>
                            </TableCell>
                          )}
                          {isColumnVisible('actions') && (
                          <TableCell className="text-right">
                            <div className="flex gap-1 justify-end">
                              <Button size="sm" variant="ghost" onClick={() => openViewDialog(payable)} title="Ver detalhes">
                                <Eye className="h-4 w-4" />
                              </Button>
                              <Button size="sm" variant="ghost" onClick={() => openEditDialog(payable)} data-testid={`edit-btn-${payable.id}`} title="Editar">
                                <Edit2 className="h-4 w-4" />
                              </Button>
                              <Button size="sm" variant="ghost" onClick={() => openReplicateDialog(payable)} title="Replicar">
                                <Copy className="h-4 w-4" />
                              </Button>
                              {(payable.status === 'pending' || payable.status === 'partially_paid') && (
                                <Button size="sm" variant="outline" onClick={() => openPayDialog(payable)} data-testid={`pay-btn-${payable.id}`}>
                                  <Check className="h-4 w-4 mr-1" />Pagar
                                </Button>
                              )}
                              {(payable.status === 'paid' || payable.status === 'partially_paid') && (
                                <Button size="sm" variant="outline" className="text-amber-600 hover:text-amber-700 border-amber-300" onClick={() => { setSelectedPayable(payable); setIsReverseOpen(true); }} title="Estornar pagamento">
                                  <RotateCcw className="h-4 w-4 mr-1" />Estornar
                                </Button>
                              )}
                              {payable.is_remaining_balance && (
                                <Badge variant="outline" className="text-xs text-amber-600 border-amber-300 ml-1">Saldo Residual</Badge>
                              )}
                              {isAdmin && <Button size="sm" variant="ghost" className="text-red-600 hover:text-red-700" onClick={() => openDeleteDialog(payable)} title="Excluir">
                                <Trash2 className="h-4 w-4" />
                              </Button>}
                            </div>
                          </TableCell>
                          )}
                        </TableRow>
                      );
                    }
                  })
                )}
              </TableBody>
            </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Edit Single Payable Dialog */}
      <Dialog open={isEditOpen} onOpenChange={setIsEditOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Editar Conta a Pagar</DialogTitle>
            {selectedPayable?.status === 'paid' && (
              <Badge variant="secondary" className="mt-2 w-fit">Conta já paga - alterações serão registradas</Badge>
            )}
          </DialogHeader>
          <form onSubmit={handleEdit}>
            <div className="grid gap-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Fornecedor</Label>
                  <div className="flex gap-2">
                    <Select 
                      value={editFormData.vendor_id || ''} 
                      onValueChange={(v) => {
                        const vendor = vendors.find(vn => vn.id === v);
                        setEditFormData({...editFormData, vendor_id: v, vendor_name: vendor?.name || ''});
                      }}
                    >
                      <SelectTrigger className="flex-1">
                        <SelectValue placeholder={editFormData.vendor_name || "Selecione..."} />
                      </SelectTrigger>
                      <SelectContent>
                        {vendors.map(v => (<SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>))}
                      </SelectContent>
                    </Select>
                    <Button type="button" variant="outline" size="icon" onClick={() => setIsCreateVendorOpen(true)} title="Criar fornecedor">
                      <Plus className="h-4 w-4" />
                    </Button>
                  </div>
                  <Input 
                    value={editFormData.vendor_name || ''} 
                    onChange={(e) => setEditFormData({...editFormData, vendor_name: e.target.value, vendor_id: ''})} 
                    placeholder="Ou digite o nome..." 
                    className="text-sm h-8"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Categoria</Label>
                  <div className="flex gap-2">
                    <Select value={editFormData.category_id || ''} onValueChange={(v) => setEditFormData({...editFormData, category_id: v})}>
                      <SelectTrigger className="flex-1"><SelectValue placeholder="Selecione..." /></SelectTrigger>
                      <SelectContent>{categories.map(c => (<SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>))}</SelectContent>
                    </Select>
                    <Button type="button" variant="outline" size="icon" onClick={() => setIsCreateCategoryOpen(true)} title="Criar categoria">
                      <Plus className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </div>
              <div className="space-y-2">
                <Label>Descrição</Label>
                <Input value={editFormData.description || ''} onChange={(e) => setEditFormData({...editFormData, description: e.target.value})} />
              </div>
              
              {/* Invoice data */}
              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>Nº do Documento</Label>
                  <Input value={editFormData.invoice_number || ''} onChange={(e) => setEditFormData({...editFormData, invoice_number: e.target.value})} />
                </div>
                <div className="space-y-2">
                  <Label>Data Emissão Doc.</Label>
                  <Input type="date" value={editFormData.invoice_date || ''} onChange={(e) => setEditFormData({...editFormData, invoice_date: e.target.value})} />
                </div>
                <div className="space-y-2">
                  <Label>Competência</Label>
                  <Input type="date" value={editFormData.competence_date || ''} onChange={(e) => setEditFormData({...editFormData, competence_date: e.target.value})} />
                </div>
              </div>
              
              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>Vencimento</Label>
                  <Input type="date" value={editFormData.due_date || ''} onChange={(e) => setEditFormData({...editFormData, due_date: e.target.value})} />
                </div>
                <div className="space-y-2">
                  <Label>Previsão Pgto</Label>
                  <Input type="date" value={editFormData.forecast_date || ''} onChange={(e) => setEditFormData({...editFormData, forecast_date: e.target.value})} title="Data prevista para pagamento" />
                </div>
                <div className="space-y-2">
                  <Label>Valor (R$) {selectedPayable?.status === 'paid' && <span className="text-xs text-muted-foreground">(bloqueado)</span>}</Label>
                  <Input 
                    type="number" 
                    step="0.01" 
                    value={editFormData.amount || ''} 
                    onChange={(e) => setEditFormData({...editFormData, amount: e.target.value})} 
                    disabled={selectedPayable?.status === 'paid'}
                    className={selectedPayable?.status === 'paid' ? 'bg-muted cursor-not-allowed' : ''}
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Centro de Custo *</Label>
                  <div className="flex gap-2">
                    <Select value={editFormData.cost_center_id || ''} onValueChange={(v) => setEditFormData({...editFormData, cost_center_id: v})}>
                      <SelectTrigger className="flex-1"><SelectValue placeholder="Selecione..." /></SelectTrigger>
                      <SelectContent>{costCenters.map(c => (<SelectItem key={c.id} value={c.id}>{c.code ? `${c.code} - ` : ''}{c.name}</SelectItem>))}</SelectContent>
                    </Select>
                    <Button type="button" variant="outline" size="icon" onClick={() => setIsCreateCostCenterOpen(true)} title="Criar centro de custo">
                      <Plus className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Forma de Pagamento</Label>
                  <Select value={editFormData.payment_method || ''} onValueChange={(v) => setEditFormData({...editFormData, payment_method: v})}>
                    <SelectTrigger><SelectValue placeholder="Selecione..." /></SelectTrigger>
                    <SelectContent>{PAYMENT_METHODS.map(m => (<SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>))}</SelectContent>
                  </Select>
                </div>
              </div>
              
              {/* Bank account - DESTAQUE para maior visibilidade */}
              <div className="space-y-2 bg-blue-50 p-3 rounded-lg border border-blue-200">
                <Label className="font-medium">Conta Bancária (para pagamento)</Label>
                <Select value={editFormData.bank_account_id || 'none'} onValueChange={(v) => setEditFormData({...editFormData, bank_account_id: v === 'none' ? '' : v})}>
                  <SelectTrigger><SelectValue placeholder="Selecione a conta bancária..." /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Nenhuma</SelectItem>
                    {bankAccounts.map(b => (
                      <SelectItem key={b.id} value={b.id}>{b.bank_name} - {b.account_number}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              {/* Payment fields - shown for paid accounts */}
              {selectedPayable?.status === 'paid' && (
                <>
                  <Separator />
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Data do Pagamento</Label>
                      <Input type="date" value={editFormData.payment_date || ''} onChange={(e) => setEditFormData({...editFormData, payment_date: e.target.value})} />
                    </div>
                  </div>
                </>
              )}
              
              <div className="space-y-2">
                <Label>Observações</Label>
                <Textarea value={editFormData.notes} onChange={(e) => setEditFormData({...editFormData, notes: e.target.value})} rows={2} />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setIsEditOpen(false)}>Cancelar</Button>
              {/* Botão "Parcelar restante": permite negociar parcelamento do saldo
                  em N vezes sem precisar replicar parcela a parcela manualmente. */}
              {selectedPayable && selectedPayable.status !== 'paid' && (
                <Button
                  type="button"
                  variant="outline"
                  className="border-amber-300 text-amber-700 hover:bg-amber-50"
                  onClick={() => { setIsEditOpen(false); setIsParcelOpen(true); }}
                  data-testid="open-parcel-dialog-btn"
                >
                  Parcelar restante
                </Button>
              )}
              <Button type="submit" disabled={isSubmitting} data-testid="save-edit-btn">
                {isSubmitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Salvar
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Installment Group Dialog */}
      <Dialog open={isInstallmentEditOpen} onOpenChange={setIsInstallmentEditOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Editar Parcelas</DialogTitle>
            <DialogDescription>
              Parcela {selectedPayable?.installment_number}/{selectedPayable?.installment_total} - {selectedPayable?.description}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Escopo da alteração</Label>
              <Select value={installmentEditData.scope} onValueChange={(v) => setInstallmentEditData({...installmentEditData, scope: v})}>
                <SelectTrigger data-testid="edit-scope-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="single">Somente esta parcela</SelectItem>
                  <SelectItem value="future">Esta e as próximas (não pagas)</SelectItem>
                  <SelectItem value="all_unpaid">Todas não pagas do grupo</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Separator />
            <div className="text-sm font-medium text-muted-foreground">Campos a alterar (deixe em branco para manter valor atual)</div>
            
            {/* Fornecedor e Categoria */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Fornecedor</Label>
                <Input value={installmentEditData.vendor_name} onChange={(e) => setInstallmentEditData({...installmentEditData, vendor_name: e.target.value})} placeholder="Manter atual" />
              </div>
              <div className="space-y-2">
                <Label>Categoria</Label>
                <Select value={installmentEditData.category} onValueChange={(v) => setInstallmentEditData({...installmentEditData, category: v})}>
                  <SelectTrigger><SelectValue placeholder="Manter atual" /></SelectTrigger>
                  <SelectContent>{categories.map(c => (<SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>))}</SelectContent>
                </Select>
              </div>
            </div>
            
            {/* Conta Bancária - MOVIDO PARA CIMA para maior visibilidade */}
            <div className="space-y-2 bg-blue-50 p-3 rounded-lg border border-blue-200">
              <Label className="font-medium">Conta Bancária (para pagamento)</Label>
              <Select value={installmentEditData.bank_account_id || "keep_current"} onValueChange={(v) => setInstallmentEditData({...installmentEditData, bank_account_id: v === "keep_current" ? "" : v})}>
                <SelectTrigger><SelectValue placeholder="Selecione para alterar..." /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="keep_current">Manter atual</SelectItem>
                  {bankAccounts.filter(b => b.id).map(b => (<SelectItem key={b.id} value={b.id}>{b.bank_name} - {b.account_number}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
            
            {/* Descrição */}
            <div className="space-y-2">
              <Label>Descrição</Label>
              <Input value={installmentEditData.description} onChange={(e) => setInstallmentEditData({...installmentEditData, description: e.target.value})} placeholder="Manter atual" />
            </div>
            
            {/* Documento */}
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>Nº do Documento</Label>
                <Input value={installmentEditData.invoice_number} onChange={(e) => setInstallmentEditData({...installmentEditData, invoice_number: e.target.value})} placeholder="Manter atual" />
              </div>
              <div className="space-y-2">
                <Label>Data Emissão</Label>
                <Input type="date" value={installmentEditData.invoice_date} onChange={(e) => setInstallmentEditData({...installmentEditData, invoice_date: e.target.value})} />
              </div>
              <div className="space-y-2">
                <Label>Competência</Label>
                <Input type="date" value={installmentEditData.competence_date} onChange={(e) => setInstallmentEditData({...installmentEditData, competence_date: e.target.value})} />
              </div>
            </div>
            
            {/* Datas e Valor */}
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>Vencimento</Label>
                <Input type="date" value={installmentEditData.due_date} onChange={(e) => setInstallmentEditData({...installmentEditData, due_date: e.target.value})} />
              </div>
              <div className="space-y-2">
                <Label>Previsão Pgto</Label>
                <Input type="date" value={installmentEditData.forecast_date} onChange={(e) => setInstallmentEditData({...installmentEditData, forecast_date: e.target.value})} />
              </div>
              <div className="space-y-2">
                <Label>Valor (R$)</Label>
                <Input type="number" step="0.01" value={installmentEditData.amount} onChange={(e) => setInstallmentEditData({...installmentEditData, amount: e.target.value})} placeholder="Manter atual" />
              </div>
            </div>
            
            {/* Centro de Custo e Forma de Pagamento */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Centro de Custo *</Label>
                <Select value={installmentEditData.cost_center_id} onValueChange={(v) => setInstallmentEditData({...installmentEditData, cost_center_id: v})}>
                  <SelectTrigger><SelectValue placeholder="Manter atual" /></SelectTrigger>
                  <SelectContent>{costCenters.map(c => (<SelectItem key={c.id} value={c.id}>{c.code ? `${c.code} - ` : ''}{c.name}</SelectItem>))}</SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Forma de Pagamento</Label>
                <Select value={installmentEditData.payment_method} onValueChange={(v) => setInstallmentEditData({...installmentEditData, payment_method: v})}>
                  <SelectTrigger><SelectValue placeholder="Manter atual" /></SelectTrigger>
                  <SelectContent>{PAYMENT_METHODS.map(m => (<SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>))}</SelectContent>
                </Select>
              </div>
            </div>
            
            {/* Observações */}
            <div className="space-y-2">
              <Label>Observações</Label>
              <Textarea value={installmentEditData.notes} onChange={(e) => setInstallmentEditData({...installmentEditData, notes: e.target.value})} rows={2} placeholder="Manter atual" />
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setIsInstallmentEditOpen(false)}>Cancelar</Button>
            <Button onClick={handleInstallmentEdit} disabled={isSubmitting} data-testid="save-installment-edit-btn">
              {isSubmitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Aplicar Alterações
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Pay Dialog */}
      <Dialog open={isPayOpen} onOpenChange={(open) => {
        // Re-fetch contas bancárias ao abrir o dialog — captura contas criadas
        // em outra aba/sessão sem precisar recarregar a página.
        if (open) { try { fetchBankAccounts(); } catch {} }
        setIsPayOpen(open);
      }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Registrar Pagamento</DialogTitle>
            <DialogDescription>{selectedPayable?.description} - Valor Original: {formatCurrency(selectedPayable?.amount || 0)}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Data do Pagamento *</Label>
              <Input type="date" value={payFormData.paid_date} onChange={(e) => setPayFormData({...payFormData, paid_date: e.target.value})} required />
            </div>
            <div className="space-y-2">
              <Label>Conta Bancária *</Label>
              <Select value={payFormData.bank_account_id} onValueChange={(v) => setPayFormData({...payFormData, bank_account_id: v})} required>
                <SelectTrigger className={!payFormData.bank_account_id ? 'border-red-300' : ''}><SelectValue placeholder="Selecione a conta..." /></SelectTrigger>
                <SelectContent>{bankAccounts.map(b => (<SelectItem key={b.id} value={b.id}>{b.bank_name} - {b.account_number}</SelectItem>))}</SelectContent>
              </Select>
              {!payFormData.bank_account_id && <p className="text-xs text-red-500">Obrigatório selecionar a conta bancária</p>}
            </div>
            
            {/* Multa, Juros e Desconto */}
            <div className="grid grid-cols-3 gap-2">
              <div className="space-y-1">
                <Label className="text-xs">Multa</Label>
                <Input 
                  type="number" 
                  step="0.01" 
                  placeholder="0,00"
                  value={payFormData.penalty_amount} 
                  onChange={(e) => setPayFormData({...payFormData, penalty_amount: e.target.value})} 
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Juros</Label>
                <Input 
                  type="number" 
                  step="0.01" 
                  placeholder="0,00"
                  value={payFormData.interest_amount} 
                  onChange={(e) => setPayFormData({...payFormData, interest_amount: e.target.value})} 
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Desconto</Label>
                <Input 
                  type="number" 
                  step="0.01" 
                  placeholder="0,00"
                  value={payFormData.discount_amount} 
                  onChange={(e) => setPayFormData({...payFormData, discount_amount: e.target.value})} 
                />
              </div>
            </div>
            
            {/* Valor pago calculado */}
            <div className="p-3 bg-muted rounded-lg">
              <div className="flex justify-between text-sm">
                <span>Valor Original:</span>
                <span>{formatCurrency(selectedPayable?.amount || 0)}</span>
              </div>
              {(payFormData.penalty_amount || payFormData.interest_amount) && (
                <div className="flex justify-between text-sm text-red-600">
                  <span>+ Multa/Juros:</span>
                  <span>{formatCurrency((parseFloat(payFormData.penalty_amount) || 0) + (parseFloat(payFormData.interest_amount) || 0))}</span>
                </div>
              )}
              {payFormData.discount_amount && (
                <div className="flex justify-between text-sm text-green-600">
                  <span>- Desconto:</span>
                  <span>{formatCurrency(parseFloat(payFormData.discount_amount) || 0)}</span>
                </div>
              )}
              <div className="flex justify-between font-semibold border-t pt-1 mt-1">
                <span>Total a Pagar:</span>
                <span>{formatCurrency(
                  (selectedPayable?.amount || 0) + 
                  (parseFloat(payFormData.penalty_amount) || 0) + 
                  (parseFloat(payFormData.interest_amount) || 0) - 
                  (parseFloat(payFormData.discount_amount) || 0)
                )}</span>
              </div>
            </div>
            
            <div className="space-y-1">
              <Label className="text-xs">Observações</Label>
              <Input 
                placeholder="Observações do pagamento..."
                value={payFormData.notes} 
                onChange={(e) => setPayFormData({...payFormData, notes: e.target.value})} 
              />
            </div>
            
            {/* Opção para não criar transação bancária automática */}
            <div className="flex items-center space-x-2 pt-2 border-t">
              <Checkbox 
                id="skip_bank_txn"
                checked={payFormData.skip_bank_transaction}
                onCheckedChange={(checked) => setPayFormData({...payFormData, skip_bank_transaction: checked})}
              />
              <label 
                htmlFor="skip_bank_txn" 
                className="text-sm text-muted-foreground cursor-pointer"
              >
                Não criar transação bancária automática (uso extrato importado)
              </label>
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setIsPayOpen(false)}>Cancelar</Button>
            <Button onClick={handlePay} disabled={isSubmitting || !payFormData.bank_account_id} data-testid="confirm-pay-btn">
              {isSubmitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Confirmar Pagamento
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Dialog */}
      <Dialog open={isViewOpen} onOpenChange={setIsViewOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Detalhes da Conta</DialogTitle>
          </DialogHeader>
          {selectedPayable && (
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-muted-foreground">Fornecedor</span>
                  <p className="font-medium">{selectedPayable.vendor_name}</p>
                </div>
                <div>
                  <span className="text-muted-foreground">Categoria</span>
                  <p className="font-medium">{selectedPayable.category || '-'}</p>
                </div>
                <div className="col-span-2">
                  <span className="text-muted-foreground">Descrição</span>
                  <p className="font-medium">{selectedPayable.description}</p>
                </div>
                {selectedPayable.invoice_number && (
                  <div>
                    <span className="text-muted-foreground">Nº do Documento</span>
                    <p className="font-medium">{selectedPayable.invoice_number}</p>
                  </div>
                )}
                {selectedPayable.competence_date && (
                  <div>
                    <span className="text-muted-foreground">Competência</span>
                    <p className="font-medium">{formatDate(selectedPayable.competence_date)}</p>
                  </div>
                )}
                <div>
                  <span className="text-muted-foreground">Vencimento</span>
                  <p className="font-medium">{formatDate(selectedPayable.due_date)}</p>
                </div>
                <div>
                  <span className="text-muted-foreground">Valor</span>
                  <p className="font-medium font-mono">{formatCurrency(selectedPayable.amount)}</p>
                </div>
                <div>
                  <span className="text-muted-foreground">Status</span>
                  <Badge className={getStatusBadgeClass(selectedPayable.status)}>
                    {getStatusLabel(selectedPayable.status)}
                  </Badge>
                </div>
                {selectedPayable.payment_date && (
                  <>
                    <div>
                      <span className="text-muted-foreground">Data Pagamento</span>
                      <p className="font-medium">{formatDate(selectedPayable.payment_date)}</p>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Conta Bancária</span>
                      <p className="font-medium">{selectedPayable.bank_account_name || '-'}</p>
                    </div>
                  </>
                )}
                {selectedPayable.installment_number && (
                  <div>
                    <span className="text-muted-foreground">Parcela</span>
                    <p className="font-medium">{selectedPayable.installment_number}/{selectedPayable.installment_total}</p>
                  </div>
                )}
                {selectedPayable.notes && (
                  <div className="col-span-2">
                    <span className="text-muted-foreground">Observações</span>
                    <p className="font-medium">{selectedPayable.notes}</p>
                  </div>
                )}
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsViewOpen(false)}>Fechar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={isDeleteOpen} onOpenChange={setIsDeleteOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir Conta a Pagar</AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja excluir esta conta?
              <div className="mt-2 p-3 bg-muted rounded-lg">
                <p className="font-medium">{selectedPayable?.description}</p>
                <p className="text-sm">{selectedPayable?.vendor_name} • {formatCurrency(selectedPayable?.amount || 0)}</p>
                {selectedPayable?.recurring_group_id && (
                  <Badge variant="outline" className="mt-1">Despesa Recorrente</Badge>
                )}
                {selectedPayable?.installment_group_id && (
                  <Badge variant="outline" className="mt-1">Parcelamento</Badge>
                )}
              </div>
              
              {/* Options to delete recurring/installments */}
              {(selectedPayable?.recurring_group_id || selectedPayable?.installment_group_id) && selectedPayable?.status !== 'paid' && (
                <div className="mt-4 space-y-3">
                  <p className="text-sm font-medium text-foreground">Opções de exclusão em grupo:</p>
                  
                  {/* Delete past unpaid */}
                  <div className="p-3 border rounded-lg flex items-center justify-between">
                    <div className="space-y-0.5">
                      <Label className="text-sm font-medium">Excluir anteriores não pagas?</Label>
                      <p className="text-xs text-muted-foreground">Remove esta e todas as anteriores não pagas (limpar histórico)</p>
                    </div>
                    <Switch 
                      checked={deleteRecurringPast} 
                      onCheckedChange={setDeleteRecurringPast} 
                    />
                  </div>
                  
                  {/* Delete future unpaid */}
                  <div className="p-3 border rounded-lg flex items-center justify-between">
                    <div className="space-y-0.5">
                      <Label className="text-sm font-medium">Excluir próximas a vencer?</Label>
                      <p className="text-xs text-muted-foreground">Remove esta e todas as futuras não pagas</p>
                    </div>
                    <Switch 
                      checked={deleteRecurringFuture} 
                      onCheckedChange={setDeleteRecurringFuture} 
                    />
                  </div>
                </div>
              )}
              
              <p className="mt-3 text-amber-600">Esta ação não pode ser desfeita.</p>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700" disabled={isSubmitting}>
              {isSubmitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {(deleteRecurringFuture && deleteRecurringPast) ? 'Excluir Todas Não Pagas' : 
               deleteRecurringFuture ? 'Excluir Futuras' : 
               deleteRecurringPast ? 'Excluir Anteriores' : 'Excluir'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Reverse Payment Confirmation Dialog */}
      <AlertDialog open={isReverseOpen} onOpenChange={setIsReverseOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <RotateCcw className="h-5 w-5 text-amber-600" />
              Estornar Pagamento
            </AlertDialogTitle>
            <AlertDialogDescription>
              <p className="mb-3">Esta ação irá reverter o pagamento, retornando a conta ao status "Pendente".</p>
              <div className="p-3 bg-muted rounded-lg">
                <p className="font-medium">{selectedPayable?.description}</p>
                <p className="text-sm">{selectedPayable?.vendor_name} • {formatCurrency(selectedPayable?.paid_amount || selectedPayable?.amount || 0)}</p>
                <p className="text-sm text-muted-foreground mt-1">
                  Pago em: {selectedPayable?.payment_date ? formatDate(selectedPayable.payment_date) : '-'}
                </p>
              </div>
              <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-lg text-amber-800 text-sm">
                <p className="font-medium">O que acontecerá:</p>
                <ul className="list-disc ml-4 mt-1 space-y-1">
                  <li>O status voltará para "Pendente"</li>
                  <li>A data de pagamento será removida</li>
                  <li>A transação bancária automática será excluída (se existir)</li>
                  <li>Você poderá editar o valor e data novamente</li>
                </ul>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleReverse} className="bg-amber-600 hover:bg-amber-700" disabled={isSubmitting}>
              {isSubmitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Confirmar Estorno
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Bulk Delete Confirmation Dialog */}
      <AlertDialog open={isBulkDeleteOpen} onOpenChange={setIsBulkDeleteOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir {selectedItems.length} Conta(s)</AlertDialogTitle>
            <AlertDialogDescription>
              <p>Tem certeza que deseja excluir as contas selecionadas?</p>
              <div className="mt-3 p-3 bg-muted rounded-lg">
                <p className="font-medium">{selectedItems.length} item(s) selecionado(s)</p>
                <p className="text-sm">Total: <span className="font-mono font-medium">{formatCurrency(getSelectedTotal())}</span></p>
              </div>
              <p className="mt-3 text-amber-600">Esta ação não pode ser desfeita.</p>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleBulkDelete} 
              className="bg-red-600 hover:bg-red-700" 
              disabled={isSubmitting}
            >
              {isSubmitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Excluir {selectedItems.length} Conta(s)
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Bulk Edit Dialog */}
      <Dialog open={isBulkEditOpen} onOpenChange={setIsBulkEditOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Editar {selectedItems.length} Conta(s) em Lote</DialogTitle>
            <DialogDescription>
              Preencha apenas os campos que deseja atualizar. Campos vazios serão ignorados.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="p-3 bg-muted rounded-lg">
              <p className="font-medium">{selectedItems.length} item(s) selecionado(s)</p>
              <p className="text-sm">Total: <span className="font-mono font-medium">{formatCurrency(getSelectedTotal())}</span></p>
            </div>

            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <DollarSign className="h-4 w-4 text-green-600" />
                Valor (R$)
              </Label>
              <Input 
                type="number" 
                step="0.01"
                min="0"
                placeholder="Deixe vazio para manter valores atuais"
                value={bulkEditData.amount} 
                onChange={(e) => setBulkEditData({...bulkEditData, amount: e.target.value})}
                data-testid="bulk-edit-amount"
              />
              <p className="text-xs text-muted-foreground">Definir o mesmo valor para todas as contas selecionadas</p>
            </div>
            
            <div className="space-y-2">
              <Label>Categoria</Label>
              <Select value={bulkEditData.category_id || "_keep"} onValueChange={(v) => setBulkEditData({...bulkEditData, category_id: v === "_keep" ? "" : v})}>
                <SelectTrigger>
                  <SelectValue placeholder="Manter atual" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="_keep">Manter atual</SelectItem>
                  {categories.map(c => <SelectItem key={c.id} value={c.id}>{c.code} - {c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Centro de Custo</Label>
              <Select value={bulkEditData.cost_center_id || "_keep"} onValueChange={(v) => setBulkEditData({...bulkEditData, cost_center_id: v === "_keep" ? "" : v})}>
                <SelectTrigger>
                  <SelectValue placeholder="Manter atual" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="_keep">Manter atual</SelectItem>
                  {costCenters.map(c => <SelectItem key={c.id} value={c.id}>{c.code ? `${c.code} - ` : ''}{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Data de Previsão</Label>
              <Input 
                type="date" 
                value={bulkEditData.forecast_date} 
                onChange={(e) => setBulkEditData({...bulkEditData, forecast_date: e.target.value})}
              />
              <p className="text-xs text-muted-foreground">Data prevista para pagamento (usada no fluxo de caixa)</p>
            </div>

            <div className="space-y-2">
              <Label>Data de Competência</Label>
              <Input 
                type="date" 
                value={bulkEditData.competence_date} 
                onChange={(e) => setBulkEditData({...bulkEditData, competence_date: e.target.value})}
              />
              <p className="text-xs text-muted-foreground">Mês/ano de referência da despesa</p>
            </div>

            <div className="space-y-2">
              <Label>Observações</Label>
              <Textarea 
                placeholder="Adicionar observação aos itens selecionados..."
                value={bulkEditData.notes} 
                onChange={(e) => setBulkEditData({...bulkEditData, notes: e.target.value})}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsBulkEditOpen(false)}>Cancelar</Button>
            <Button onClick={handleBulkEdit} disabled={isSubmitting}>
              {isSubmitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Atualizar {selectedItems.length} Conta(s)
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Pay Dialog */}
      <Dialog open={isBulkPayOpen} onOpenChange={setIsBulkPayOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <DollarSign className="h-5 w-5 text-green-600" />
              Pagar em Lote
            </DialogTitle>
            <DialogDescription>
              Registrar pagamento de {selectedItems.length} conta(s) selecionada(s)
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
              <p className="font-medium text-green-800">
                Total a pagar: {formatCurrency(getSelectedTotal())}
              </p>
              <p className="text-sm text-green-700 mt-1">
                {selectedItems.length} conta(s) pendente(s)
              </p>
            </div>

            <div className="space-y-2">
              <Label>Conta Bancária *</Label>
              <Select 
                value={bulkPayData.bank_account_id} 
                onValueChange={(v) => setBulkPayData({...bulkPayData, bank_account_id: v})}
              >
                <SelectTrigger data-testid="bulk-pay-bank-select">
                  <SelectValue placeholder="Selecione a conta..." />
                </SelectTrigger>
                <SelectContent>
                  {bankAccounts.map(acc => (
                    <SelectItem key={acc.id} value={acc.id}>
                      {acc.bank_name} - {acc.account_number}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Data do Pagamento</Label>
              <Input 
                type="date" 
                value={bulkPayData.payment_date} 
                onChange={(e) => setBulkPayData({...bulkPayData, payment_date: e.target.value})}
              />
            </div>

            <div className="space-y-2">
              <Label>Forma de Pagamento</Label>
              <Select 
                value={bulkPayData.payment_method} 
                onValueChange={(v) => setBulkPayData({...bulkPayData, payment_method: v})}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PAYMENT_METHODS.map(m => (
                    <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsBulkPayOpen(false)}>Cancelar</Button>
            <Button 
              onClick={handleBulkPay} 
              disabled={isSubmitting || !bulkPayData.bank_account_id}
              className="bg-green-600 hover:bg-green-700"
            >
              {isSubmitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Pagar {selectedItems.length} Conta(s)
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Replicate Dialog */}
      <Dialog open={isReplicateOpen} onOpenChange={setIsReplicateOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Replicar Conta a Pagar</DialogTitle>
            <DialogDescription>
              Uma cópia desta conta será criada. Altere os campos desejados.
            </DialogDescription>
          </DialogHeader>
          {selectedPayable && (
            <div className="space-y-4 py-4">
              <div className="p-3 bg-muted rounded-lg">
                <p className="text-sm text-muted-foreground">
                  {selectedPayable.vendor_name} • Original: {formatCurrency(selectedPayable.amount)}
                </p>
              </div>
              <div className="space-y-2">
                <Label>Descrição</Label>
                <Input
                  value={replicateDescription}
                  onChange={(e) => setReplicateDescription(e.target.value)}
                  data-testid="replicate-description"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label>Valor (R$)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={replicateAmount}
                    onChange={(e) => setReplicateAmount(e.target.value)}
                    data-testid="replicate-amount"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Nova Data de Vencimento *</Label>
                  <Input
                    type="date"
                    value={replicateDate}
                    onChange={(e) => setReplicateDate(e.target.value)}
                    data-testid="replicate-due-date"
                  />
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsReplicateOpen(false)}>Cancelar</Button>
            <Button onClick={handleReplicate} disabled={isSubmitting || !replicateDate} data-testid="replicate-confirm-btn">
              {isSubmitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              <Copy className="h-4 w-4 mr-2" />
              Replicar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Parcelar Restante Dialog */}
      <Dialog open={isParcelOpen} onOpenChange={setIsParcelOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Parcelar saldo</DialogTitle>
            <DialogDescription>
              Divida este lançamento em N parcelas. A conta atual vira "Parcela 1/N" e as demais são criadas como cópias no mesmo grupo.
            </DialogDescription>
          </DialogHeader>
          {selectedPayable && (
            <div className="space-y-4 py-2">
              <div className="rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 text-sm">
                <div className="font-medium text-amber-900">{selectedPayable.vendor_name}</div>
                <div className="text-amber-800">
                  Total a parcelar: <strong className="font-mono">{formatCurrency(parcelOriginalAmount)}</strong>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label>Nº de parcelas</Label>
                  <Input
                    type="number"
                    min={2}
                    max={120}
                    value={parcelCount}
                    onChange={(e) => setParcelCount(Math.max(2, Math.min(120, parseInt(e.target.value) || 2)))}
                    data-testid="parcel-count"
                  />
                </div>
                <div className="space-y-1">
                  <Label>1ª parcela em</Label>
                  <Input
                    type="date"
                    value={parcelFirstDate}
                    onChange={(e) => setParcelFirstDate(e.target.value)}
                    data-testid="parcel-first-date"
                  />
                </div>
              </div>

              <div className="flex items-center justify-between rounded-md border px-3 py-2">
                <Label htmlFor="parcel-equal" className="text-sm cursor-pointer">Valores iguais (dividir automaticamente)</Label>
                <input
                  id="parcel-equal"
                  type="checkbox"
                  checked={parcelUseEqual}
                  onChange={(e) => setParcelUseEqual(e.target.checked)}
                  data-testid="parcel-use-equal"
                />
              </div>

              <div className="space-y-1">
                <Label className="text-xs">Valores das parcelas</Label>
                <div className="grid grid-cols-2 gap-2 max-h-48 overflow-y-auto pr-1">
                  {parcelAmounts.map((v, idx) => (
                    <div key={idx} className="flex items-center gap-2">
                      <span className="text-xs text-muted-foreground w-10">{idx + 1}/{parcelCount}</span>
                      <Input
                        type="number"
                        step="0.01"
                        value={v}
                        disabled={parcelUseEqual}
                        onChange={(e) => {
                          const next = [...parcelAmounts];
                          next[idx] = parseFloat(e.target.value) || 0;
                          setParcelAmounts(next);
                        }}
                        className="font-mono text-right"
                      />
                    </div>
                  ))}
                </div>
                <div className={`text-xs font-mono flex items-center justify-between mt-1 px-2 py-1 rounded ${
                  Math.abs(parcelDiff) < 0.01
                    ? 'bg-emerald-50 text-emerald-700'
                    : 'bg-rose-50 text-rose-700 font-semibold'
                }`}>
                  <span>Soma das parcelas:</span>
                  <span>{formatCurrency(parcelSum)} {Math.abs(parcelDiff) >= 0.01 && `(diff ${parcelDiff > 0 ? '+' : ''}${formatCurrency(parcelDiff)})`}</span>
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsParcelOpen(false)}>Cancelar</Button>
            <Button
              onClick={handleParcel}
              disabled={isSubmitting || !parcelFirstDate || Math.abs(parcelDiff) > 0.01}
              data-testid="parcel-confirm-btn"
              className="bg-amber-600 hover:bg-amber-700"
            >
              {isSubmitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Parcelar em {parcelCount}x
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PayablesPage;
